import { Banners } from '../announcements/models';
import { TitleImages } from '../../backbone/features/mobileForm/BaseField/views';
import { ExportSensorTempLogReport } from '../../backbone-unorganized/sensors/models';
import { AccountSubmissions, MobileSubmission } from '../../backbone/features/mobileForm/MobileFormStructure/models';
import { HistoryListItem, SubmittedFormPage } from '../../backbone/features/mobileForm/views';
import { EmptyListMessage } from '../../backbone/shared/views/EmptyListMessage';
import { BaseToolbarNavPageLayout } from '../../backbone/shared/views/baseLayoutViews';
import { ToolbarNav } from '../../backbone/features/toolbarNav/views';
import { BaseTabbedLayoutPage, TabbedLayout } from '../../backbone/shared/views/tabs';
import {
    DashboardTaskSearchOptions,
    MobileUserRoles,
    ProjectTaskSearchOptions,
    TabbedLayoutPageList,
    TaskSearchOptions
} from '../models';
import { BaseZenputCollection, global_recurring_utils, SearchCriteria, SearchCriterias } from '../shared/forms';
import { DashboardTasks, MobileTask } from '../tasks/models';
import { isWindows, isConnected } from '../../shared/utils/is';
import {
    hideLoadingDialog,
    showOfflineMessage,
    showSuccessMessage,
    showLoadingDialog,
    showErrorMessage
} from '../../shared/utils/notifications';
import {
    FormTemplateTypeahead,
    ProjectsView,
    SelectAccountOrGroupModal,
    TaskDetailsPage,
    TasksFilteredByProject,
    TasksLegendModal,
    TasksPage
} from '../views';
import AnnouncementBanner from '../../react/features/AnnouncementBanner';
import AnnouncementBannerDetails from '../../react/features/AnnouncementBannerDetails';
import {
    AccountsForDistributeGroup,
    DashboardCardResultListForTasksCurrent,
    DashboardFilterList,
    DashboardFilterListForLogbook,
    DashboardViewConfigurations,
    GalleryImages,
    LogbookResults,
    LogBookTaskSearchOptions,
    MobileBaseDashboardCardResultList,
    MobileRecurringProjects
} from './models';
import Carousel from '../../react/shared/Carousel';
import { ComplianceProjectCompletionRateReport } from '../../backbone/features/ComplianceProjectCompletionRateReport/views';
import { RecipientEmailSelector } from '../../backbone/features/ComplianceProjectCompletionRateReport/RecipientEmailSelector/views';
import { ZenputCodes } from '../../constants';
import { DashboardSearchStructures } from './DashboardSearchStructures';
import { pendoTrack, pendoPageLoad } from '../utils/logging';

export const DEFAULT_TEAM_DASHBOARD_TAB_ID = 'team';

export const getDefaultLocationDashboardTabId = () => {
    return $f.active_user.is_submitter() ? 'tasks' : 'summary';
};

export const DashboardToolbarNav = ToolbarNav.extend({
    /**
     * Unlike other ToolbarNav's our select options list is actually a full-page search.
     *
     * @see SelectAccountOrGroupModal
     */
    show_select_options_list: function () {
        if (!this.show_select_options()) {
            return false;
        }

        $f.mobile.nav.show_modal(SelectAccountOrGroupModal, {
            default_value: '', // never want to show a default here
            post_on_select: function (model) {
                $f.mobile.nav.show(MobileDashboard, {
                    active_account_or_group: model
                });
            }
        });

        return false;
    },

    show_select_options: function () {
        if ($f.mobile.dashboard.user_only_in_leaf() || $f.mobile.dashboard.distribute_groups.length === 0) {
            return false;
        }
        return true;
    }
});

export const MobileDashboardTabbedLayout = TabbedLayout.extend({
    /**
     * We want to track in Heap when a user views a dashboard tab
     */
    jump_to_page: function (page_id, animate, do_focus_events) {
        const previousTabId = this.active_tab_id;

        // Track previous tab exit before switching
        if ($f.flags.get_flag('pendo_mobile_dashboard_tracking')) {
            if ($f.mobile.dashboard._lastTabName && $f.mobile.dashboard._lastTabStartTime) {
                const durationSeconds = Math.round((Date.now() - $f.mobile.dashboard._lastTabStartTime) / 1000);
                pendoTrack('dashboard_tab_exit', {
                    tab_name: $f.mobile.dashboard._lastTabName,
                    duration_seconds: durationSeconds
                });
            }
        }

        MobileDashboardTabbedLayout.__super__.jump_to_page.apply(this, arguments);

        if (this.active_tab_id) {
            // keep track of changes to our active tab in our global dashboard data
            $f.mobile.dashboard.active_tab_id = this.active_tab_id;

            // Track tab switch when navigating between tabs
            if ($f.flags.get_flag('pendo_mobile_dashboard_tracking')) {
                if (previousTabId && previousTabId !== this.active_tab_id) {
                    const currentViewMode = MobileDashboard.is_location_dashboard() ? 'location' : 'team';
                    pendoTrack('dashboard_tab_switched', {
                        from_tab: previousTabId,
                        to_tab: this.active_tab_id
                    });
                    pendoPageLoad({ url: `/dashboard/${currentViewMode}/${this.active_tab_id}` });
                }

                // Update tab tracking variables globally
                $f.mobile.dashboard._lastTabStartTime = Date.now();
                $f.mobile.dashboard._lastTabName = this.active_tab_id;
            }
        }

        if (this.hide_tabs) {
            return;
        }

        if (!page_id || !animate || !do_focus_events) {
            return;
        }
    }
});

export const MobileDashboard = BaseToolbarNavPageLayout.extend(
    {
        template: '#tpl_mobile_dashboard',
        toolbarnav_class: DashboardToolbarNav,
        toolbarnav_title: '', // we'll set this dynamically in JS based on our current hierarchy
        use_transitions: true,
        is_top_level_page: false, // assume this is a drilled down page unless this is instantiated from the main nav
        reset_active_group_or_account_on_close: false, // will set the active_group_or_account to null when the dashboard closes
        auto_resize_page_scrollable: false, // our TabbedNav pages will handle this
        active_tab_id: DEFAULT_TEAM_DASHBOARD_TAB_ID,
        banners: null,

        regions: {
            r_tabbed_layout: '.tabbed_layout',
            r_banners: '.banners'
        },

        initialize: function (options) {
            options = options || {};
            $.extend(this, options);

            if (!options.do_not_set_leftnav) {
                $f.application.vent.trigger('mobile:set_active_leftmenu_item', 'nav_dashboard');
            }

            this.listenTo($f.mobile.dashboard.distribute_groups, 'sync', function () {
                $f.mobile.dashboard.distribute_groups._set_hierarchy(true, true);
            });

            var _active_account_or_group = this.get_active_account_or_group(options);

            // pass active_account_or_group in via navigation so this can be saved
            if (_active_account_or_group) {
                $f.mobile.dashboard.set_active_account_or_group(_active_account_or_group);
            } else {
                $f.mobile.dashboard.set_active_account_or_group($f.mobile.dashboard.distribute_groups.get_root_group());
            }

            // Track view mode switch
            if ($f.flags.get_flag('pendo_mobile_dashboard_tracking')) {
                const previousViewMode = $f.mobile.dashboard.current_view_mode;
                const newViewMode = MobileDashboard.is_location_dashboard() ? 'location' : 'team';

                if (previousViewMode && previousViewMode !== newViewMode) {
                    pendoTrack('dashboard_view_mode_switched', {
                        from_view_mode: previousViewMode,
                        to_view_mode: newViewMode
                    });
                }

                $f.mobile.dashboard.current_view_mode = newViewMode;
            }

            $f.mobile.settings.init_wootric();

            this.banners = new Banners();
            this.banners.fetch();

            // Track dashboard session start
            this._dashboardSessionStartTime = Date.now();
        },

        onShow: function () {
            this.render(); // @todo:marionette_showrender @jira:INF-559
        },

        onRender: function () {
            if (!this._isShown) {
                return;
            }
            var this_ = this;

            var _handle_error = function () {
                hideLoadingDialog();
                if (!this_.r_tabbed_layout) {
                    return;
                } // just in case user has left by time of error and view has close()d

                this_.r_tabbed_layout.show(
                    new EmptyListMessage({
                        i18n_key_message: 'mobile_message_show_error_message',
                        icon_class: 'df-alert',
                        allow_retry: true
                    })
                );

                this_.listenToOnce(this_.r_tabbed_layout.currentView, 'empty_view:retry_fetch', function () {
                    this_.onRender();
                });
            };

            // we need to fetch the accessible accounts to ensure the proper visibility mode for this user\
            showLoadingDialog();
            $f.mobile.dashboard.accessible_accounts.fetch({
                success: function () {
                    // set if this is a top level page
                    // if this has a parent group, allow the back button
                    var active_group = $f.mobile.dashboard.get_active_account_or_group();

                    if (active_group) {
                        hideLoadingDialog();
                        this_.render_tabs();
                        return;
                    }

                    // First we need to make sure we have an updated distribute group hierarchy so our dashboard
                    // pages will know what team we're looking at
                    $f.mobile.dashboard.distribute_groups.fetch({
                        success: function () {
                            hideLoadingDialog();
                            this_.render_tabs();
                        },
                        error: function () {
                            _handle_error();
                        }
                    });
                },
                error: function () {
                    _handle_error();
                }
            });

            if (this.banners) {
                this.r_banners.show(new BannersCarousel({ collection: this.banners }));
            }
        },

        go_back: function () {
            $f.mobile.dashboard.active_search_item_name = null;

            if ($f.flags.get_flag('pendo_mobile_dashboard_tracking')) {
                // Track tab exit with duration when leaving dashboard
                if (this._currentTabName && this._currentTabStartTime) {
                    const durationSeconds = Math.round((Date.now() - this._currentTabStartTime) / 1000);
                    pendoTrack('dashboard_tab_exit', {
                        tab_name: this._currentTabName,
                        duration_seconds: durationSeconds
                    });
                }

                // Track overall dashboard session exit with duration
                if (this._dashboardSessionStartTime) {
                    const sessionDurationSeconds = Math.round((Date.now() - this._dashboardSessionStartTime) / 1000);
                    pendoTrack('dashboard_session_exit', {
                        session_duration_seconds: sessionDurationSeconds
                    });
                }
            }

            // If going back from an account view we need to reset the tab from summary to teams
            if ($f.mobile.dashboard.get_active_account()) {
                $f.mobile.dashboard.active_tab_id = null;
            }

            $f.mobile.nav.go_back();
        },

        render_tabs: async function () {
            if (!this.r_tabbed_layout) {
                return;
            } // just in case user has left by time of error and view has close()d

            const tabId = this._determine_active_tab_id(this.options);

            // Determine current view mode (team vs location)
            const currentViewMode = MobileDashboard.is_location_dashboard() ? 'location' : 'team';

            if ($f.flags.get_flag('pendo_mobile_dashboard_tracking')) {
                // Track entry tab on first dashboard visit
                if (!this._dashboardEntryTabTracked) {
                    pendoTrack('dashboard_entry_tab', {
                        tab_name: tabId
                    });
                    this._dashboardEntryTabTracked = true;
                }

                // Track dashboard tab visit in Pendo with user context
                pendoTrack('dashboard_tab_visited', {
                    tab_name: tabId,
                    timestamp: new Date().toISOString()
                });

                // Track current dashboard location
                pendoPageLoad({ url: `/dashboard/${currentViewMode}/${tabId}` });
            }

            // allow active_tab_id to be passed in as an option so that navigation saves these options
            this.active_tab_id = $f.mobile.dashboard.active_tab_id = tabId;

            // Store tab entry time for duration tracking (instance level for go_back)
            this._currentTabStartTime = Date.now();
            this._currentTabName = tabId;

            // Store tab timing globally for jump_to_page tracking
            if ($f.flags.get_flag('pendo_mobile_dashboard_tracking')) {
                $f.mobile.dashboard._lastTabStartTime = Date.now();
                $f.mobile.dashboard._lastTabName = tabId;
            }

            // Store current view mode for switch detection (persists across view instances)
            $f.mobile.dashboard.current_view_mode = currentViewMode;

            // don't show the team tab and only show the info page for that one account if its a single account owner
            if ($f.mobile.dashboard.is_single_account_owner()) {
                $f.mobile.dashboard.set_active_account_or_group(
                    $f.mobile.dashboard.accessible_accounts.get_owned_accounts_for_active_user()[0]
                );
            }

            // Update our title to match our current hierarchy
            var account_or_group = $f.mobile.dashboard.get_active_account_or_group();

            if (!account_or_group) {
                // Unfortunately, we still need to show the TabbedLayout, because we need to show a ComponentMyTasks which
                // needs to be inside a dashboard card which needs an active tab, etc.
                this.r_tabbed_layout.show(
                    new MobileDashboardTabbedLayout({
                        tab_pages: new TabbedLayoutPageList([
                            {
                                id: 'no_groups',
                                name: null,
                                view_class: DashboardTabNoGroups
                            }
                        ]),
                        hide_tabs: true
                    })
                );
                $f.mobile.dashboard.active_tab_id = 'no_groups';
                return false;
            }

            this.toolbarnav_title = account_or_group.get('name'); // in case we re-render
            this.toolbarnav.page_title = account_or_group.get('name');
            this.toolbarnav.render();

            // dont show the leftnav locations list if a single or multi account submitter owner.
            if ($f.mobile.dashboard.is_single_account_owner() || $f.mobile.dashboard.is_multi_account_owner()) {
                $f.application.content.currentView.r_leftmenu.currentView.hide_account_link();
            }

            const tabbed_nav_page_list = await MobileDashboard.get_tabs(MobileDashboard.is_location_dashboard());

            this.r_tabbed_layout.show(
                new MobileDashboardTabbedLayout({
                    active_tab_id: $f.mobile.dashboard.active_tab_id,
                    tab_pages: new TabbedLayoutPageList(tabbed_nav_page_list)
                })
            );

            // this needs to run after the viewPager has been shown.
            this.on_orientation_change();
        },

        onClose: function () {
            // Unfortunately we are micro managing different ways to get to the task lists in this view. This view has
            // special considerations made for it in $f.mobile.nav. This means we have to check if the new view being opened
            // sets an active_account_or_group in order to avoid resetting it on this call. This call happens after the new
            // view is initialized.
            var is_account_selected = !!(
                _.last($f.mobile.nav._options) && _.last($f.mobile.nav._options).active_account_or_group
            );

            if (this.reset_active_group_or_account_on_close && !is_account_selected) {
                $f.mobile.dashboard.set_active_account_or_group(null);
            }
        },

        _determine_active_tab_id: function (options) {
            if ($f.mobile.dashboard.active_tab_id) {
                return $f.mobile.dashboard.active_tab_id;
            }

            // @author: halah when coming via routes, the data is passed in initial_page_data
            const provided_tab_id = options.active_tab_id || options.initial_page_data?.active_tab_id;

            if (provided_tab_id) {
                return provided_tab_id;
            }

            return this.active_tab_id;
        },

        get_active_account_or_group: function (options) {
            // @author: halah when coming via routes, the data is passed in initial_page_data
            return options.active_account_or_group || options?.initial_page_data?.active_account_or_group;
        }
    },
    {
        get_tabs: async (is_location_dashboard) => {
            const team = {
                id: 'team',
                name: _t('mobile_common_filter_team'),
                view_class: DashboardTabTeam
            };

            const summary = {
                id: 'summary',
                name: _t('mobile_dashboard_tab_title_summary'),
                view_class: DashboardTabSummary
            };

            const tasks = {
                id: 'tasks',
                name: _t('mobile_common_tasks'),
                view_class: DashboardTabTasks,
                view_override: true
            };

            const logbook = {
                id: 'logbook',
                name: _t('mobile_pso_filter_recurring'),
                view_class: DashboardTabLogbook
            };

            const feed = {
                id: 'feed',
                name: _t('mobile_dashboard_tab_title_activity'),
                view_class: DashboardTabFeed
            };

            const gallery = {
                id: 'gallery',
                name: _t('mobile_action_sheet_media_gallery'),
                view_class: DashboardTabGallery
            };

            const info = {
                id: 'info',
                name: _t('mobile_dashboard_tab_title_info'),
                view_class: DashboardTabInfo
            };

            const projectCompliance = {
                id: 'project_compliance',
                name: _t('mobile_dashboard_tab_project_audit'),
                view_class: ComplianceTab
            };

            const location_default_tabs = [summary, tasks, feed, logbook, gallery, info];

            const team_tabs = [team, summary, tasks, logbook];

            const apply_compliance_tab = function (tabs) {
                // Add compliance tab after the `feed` tab
                let idx = tabs.indexOf(feed);
                if (idx === -1) {
                    $f.mobile.logging.log_error('Cannot find `feed` to add tab after, defaulting to 3rd');
                    idx = 2;
                }
                tabs.splice(idx + 1, 0, projectCompliance);
                return tabs;
            };

            const apply_sensor_integration_ff = async function (tabs) {
                // Add sensors tab behind a feature flag after the `logbok` tab, defaults to 5th.
                if (!$f.active_company.has_feature('sensor_integration')) {
                    return tabs;
                }

                const { DashboardTabSensors } = await import('../sensors/views');
                const sensors = {
                    id: 'sensors',
                    name: _t('mobile_sensors_sensors'),
                    view_class: DashboardTabSensors
                };
                let idx = tabs.indexOf(logbook);
                if (idx === -1) {
                    $f.mobile.logging.log_error('Cannot find `logbook` to add sensor tab after, defaulting to 5th');
                    idx = 4;
                }
                tabs.splice(idx + 1, 0, sensors);
                return tabs;
            };

            if (is_location_dashboard) {
                return apply_sensor_integration_ff(apply_compliance_tab(location_default_tabs));
            }
            return team_tabs;
        },

        is_location_dashboard: () =>
            $f.mobile.dashboard.get_active_account() || $f.mobile.dashboard.is_single_account_owner()
    }
);

// ** Generics ** //

export const BaseDashboardTab = BaseTabbedLayoutPage.extend({
    template: '#tpl_dashboard_tab',
    className: 'tabbed_layout_page mobile_dashboard_page',
    dashboard_cards: null, // DashboardViewConfigurations
    message_below_content: null, // a message to be shown below the cards

    regions: {
        r_body: '.dashboard_tab_body'
    },

    templateHelpers: function () {
        return {
            message_below_content: this.message_below_content
        };
    },

    initialize: function () {
        BaseDashboardTab.__super__.initialize.apply(this, arguments);
        this.setup_cards();
    },

    onRender: function () {
        this.r_body.show(
            new MobileDashboardCards({
                collection: this.dashboard_cards,
                tab_id: this.__tabbed_nav_page_lists_item.get('id')
            })
        );
    },

    /**
    + Setup the dashboard_cards collection
    *
    + @return {DashboardViewConfigurations}
    */
    setup_cards: function () {
        throw new Error('Programmer Error: You must implement the setup_cards method.');
    }
});

export const MobileDashboardCards = Backbone.Marionette.CollectionView.extend({
    className: 'dashboard_card_collection',
    collection: null, // DashboardViewConfigurations,
    tab_id: null, // The tab ID we belong to

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
    },

    getItemView: function (model, idx) {
        return model.get('view_class');
    },

    itemViewOptions: function (model, idx) {
        // add the tab_id into the view options obj so it will be passed all the way down into components
        return {
            view_options: $.extend(true, {}, { tab_id: this.tab_id }, model.get('view_options'))
        };
    }
});

export const MobileDashboardCard = Backbone.Marionette.Layout.extend({
    template: '#tpl_dashboard_card',
    className: 'dashboard_card card',

    components: [],
    components_collection: null, // DashboardViewConfigurations
    model: null, // DashboardViewConfiguration
    allow_click_header: false, // If true, we will call on_header_click when user clicks card header
    view_options: {}, // this is filled with arbitrary context about the card when instantiated
    show_info_icon: false, // to show info icon next to card title
    show_header: true,

    regions: {
        r_filter: '.dashboard_card_filter',
        r_body: '.dashboard_card_body'
    },

    events: {
        'click .dashboard_card_title': '_on_header_click'
    },

    templateHelpers: function () {
        if (this.allow_click_header && this.show_info_icon) {
            // we can't do this because of an <a> within an <a>. If we want to allow this, we'll have to update our
            // template to have a more separate click area for header vs icon.
            throw new Error('PROGRAMMER ERROR: cannot have both allow_click_header and show_info_icon set to true.');
        }

        return {
            show_header: this.show_header,
            card_title: this.get_title(),
            card_subtitle: this.get_subtitle(),
            allow_click_header: this.allow_click_header,
            show_info_icon: this.show_info_icon && $f.mobile.dashboard.is_account(this.view_options.group_or_account)
        };
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
        var this_ = this;

        // 1. check if its visible, and if so, call fetch on the queries
        // - maybe always_prefetch as an option

        // 2. add listeners to changes to the current distribute group
        this.listenTo(this.model.collection, 'dashboard_card:fetch', function () {
            this_._trigger_fetch();
        });
    },

    onRender: function () {
        var this_ = this;

        if (!this.components || !this.components.length) {
            return;
        }

        this.components_collection = new DashboardViewConfigurations();
        $.each(this.components, function (idx, component_name) {
            this_.components_collection.add({
                view_class: component_name,
                view_options: this_.view_options
            });
        });

        this.r_body.show(
            new DashboardCardComponents({
                collection: this.components_collection
            })
        );

        // show the filters and titles when component data is done loading
        this.listenTo(this.r_body.currentView, 'component:query_collection_loaded', function () {
            $(this_.$el.find('.df-info')).removeClass('hidden');

            this_.show_filter_view();

            if (this_.r_filter.currentView) {
                this_.stopListening(this_.r_filter.currentView, 'filter_change');
                this_.listenTo(this_.r_filter.currentView, 'filter_change', function (selection) {
                    // Track dashboard filter usage
                    if ($f.flags.get_flag('pendo_mobile_dashboard_tracking')) {
                        pendoTrack('dashboard_filter_applied', {
                            card_name: this_.query_key,
                            tab_id: this_.tab_id
                        });
                    }

                    // change search_criterias based on selection
                    if (_.isNumber(selection)) {
                        this_.view_options.frequency = selection;
                    } else {
                        this_.view_options.form_template = selection;
                    }
                    this_.r_body.currentView.render();
                    this_._trigger_fetch();
                });
            }
        });
    },

    onShow: function () {
        // create it once its been rendered so we have a real evaluation of what's visible.
        setTimeout(this.init_intersection_observer.bind(this), 500);
    },

    /**
     * Create viewport intersection observer to watch for when this card becomes visible.
     * https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
     */
    init_intersection_observer: function () {
        var this_ = this;

        var observer = new IntersectionObserver(
            function (entries) {
                _.each(entries, function (change) {
                    if (!change.isIntersecting) {
                        return;
                    }

                    // wait a little bit in case we're switching tabs - don't want to start
                    // rendering or fetching components while we're transitioning tabs
                    setTimeout(function () {
                        if (!$f.utils.is_element_in_viewport(this_.$el)) {
                            return;
                        }

                        this_.$el.addClass('has_intersected'); // for perf, don't animate loading states until now
                        this_._trigger_fetch();
                    }, 300);
                });
            },
            {
                root: null,
                rootMargin: '40px 0px',
                threshold: 0.05 // 5% of element must be visible - @author:david prevents elements not in active tab
                // (to left/right) from triggering. Ideally we would set a negative left/right
                // rootMargin, but that doesn't fly on Windows :(
            }
        );

        observer.observe(this.el);
    },

    _trigger_fetch: function () {
        this.components_collection.trigger('dashboard_card:fetch', { from_viewport_scroll: true });
    },

    /**
     * Get the card's title
     *
     * @return {string|null} The string to use for the Title div
     */
    get_title: function () {
        return null;
    },

    /**
     * Get the card's subtitle
     *
     * @return {string|null} The string to use for the Title div
     */
    get_subtitle: function () {
        return null;
    },

    /**
     * Subclasses can add an attachment to the r_filter region here.
     */
    show_filter_view: function () {},

    on_header_click: function () {
        return false;
    }, // override in subclasses

    _on_header_click: function (event) {
        if (!this.allow_click_header) {
            return false;
        }
        $f.mobile.dashboard.active_search_item_name = null;
        this.on_header_click();
        return false;
    }
});

export const ComponentDownState = Backbone.Marionette.Layout.extend({
    template: '#tpl_dashboard_component_down_state',
    className: 'down_state',
    zenput_code: null, // Different error codes for downstates

    templateHelpers: function () {
        return {
            msg: this.get_down_state_msg_from_zenput_code(),
            icon: this.get_val_from_query_key('icon')
        };
    },

    events: {
        click: 'downstate_retry'
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
    },

    downstate_retry: function (event) {
        // If we are in an error state let the user click to retry
        if (!this.zenput_code || this.zenput_code === ZenputCodes.UNKNOWN_ERROR) {
            this.trigger('downstate_retry');
        }
    },

    query_key_info_table: {
        project_recurring_aggregate: {
            translation_noun_tag: 'mobile_projects_tag',
            icon: 'df-projects'
        },
        project_list: {
            translation_noun_tag: 'mobile_projects_tag',
            icon: 'df-projects'
        },
        forms_top_nos: {
            translation_noun_tag: 'mobile_forms_tag',
            icon: 'df-form'
        },
        tasks_current: {
            translation_noun_tag: 'mobile_tasks_tag',
            icon: 'df-tasks'
        },
        workflows_top_triggered: {
            translation_noun_tag: 'mobile_workflows_tag',
            icon: 'df-workflow'
        },
        tasks_completion_rate: {
            translation_noun_tag: 'mobile_tasks_tag',
            icon: 'df-tasks'
        },
        location_gallery: {
            // todo: @author: halah @jira:MOBILE-739 replace with ones for gallery
            translation_noun_tag: 'mobile_tasks_tag',
            icon: 'df-tasks'
        },
        default_downstate: {
            translation_noun_tag: '',
            icon: 'df-alert'
        }
    },

    get_val_from_query_key: function (key) {
        var entry = this.query_key_info_table[this.query_key] || this.query_key_info_table.default_downstate;
        return entry[key];
    },

    /**
     * Return generic messages based on the zenput codes with a catch all
     *
     * @param zenput_code
     * @returns {string}
     */
    get_down_state_msg_from_zenput_code: function () {
        if (this.zenput_code == ZenputCodes.DOWN_STATE) {
            return `${_t('mobile_common_button_no')} ${_t(this.get_val_from_query_key('translation_noun_tag'))} ${_t(
                'mobile_dashboard_configuration_down_state_postfix_msg'
            )}`;
        } else if (this.zenput_code == ZenputCodes.NO_DATA_IN_RANGE) {
            return _t('mobile_dashboard_zenput_code_no_data_in_range');
        } else if (this.zenput_code == ZenputCodes.PERMISSION_DENIED) {
            return _t('mobile_dashboard_zenput_code_permission_denied');
        } else if (this.zenput_code == ZenputCodes.NO_FORM_TEMPLATE_SELECTED) {
            return _t('mobile_dashboard_zenput_code_no_form_template_selected');
        } else {
            return _t('mobile_dashboard_zenput_code_unknown_error') + '<br/>' + _t('mobile_dashboard_tap_to_retry');
        }
    }
});

export const ComponentLoadingStateTasks = Backbone.Marionette.Layout.extend({
    template: '#tpl_loading_state_tasks',
    className: 'loading_state_tasks_container'
});

export const ComponentLoadingStateTableItem = Backbone.Marionette.Layout.extend({
    template: '#tpl_loading_state_table_items',
    className: 'loading_state_table_items_container'
});

export const ComponentLoadingStateTasksCompletionRate = Backbone.Marionette.Layout.extend({
    template: '#tpl_loading_state_tasks_completion_rate',
    className: 'loading_state_tasks_completion_rate_container'
});

export const DashboardCardComponents = Backbone.Marionette.CollectionView.extend({
    collection: null, // DashboardViewConfigurations
    itemViewEventPrefix: 'component',

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
    },

    getItemView: function (model, idx) {
        return model.get('view_class');
    },

    itemViewOptions: function (model, idx) {
        return model.get('view_options');
    }
});

export const DashboardCardComponent = Backbone.Marionette.Layout.extend({
    template: '#tpl_dashboard_card_component',

    model: null, // DashboardViewConfiguration
    query_key: null, // query type key
    query_structure: null,
    query_collection: null, // MobileBaseDashboardCardResultList
    no_query: false, // Set to true for components that do not make queries and have no loading state
    view_class: null, // LayoutView that will be passed the fetched data.
    loading_state_view_class: null, // LayoutView that will handle loading state placeholder
    tab_id: null, // the tab_id that this component lives inside
    query_collection_class: MobileBaseDashboardCardResultList,
    dashboard_filters_class: DashboardFilterList,

    className: function () {
        return this.query_key;
    },

    events: {
        'click .dashboard_card_component_body': 'on_content_click_event'
    },

    regions: {
        r_body: '.dashboard_card_component_body',
        r_down_state: '.dashboard_card_component_down_state',
        r_loading_state: '.dashboard_card_component_loading_state'
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
        var this_ = this;

        if (!this.view_class) {
            throw new Error('Programmer Error: view_class not found in DashboardSearchStructures');
        }

        if (this.no_query) {
            return false;
        }

        if (this.query_key && !DashboardSearchStructures[this.query_key]) {
            throw new Error('Programmer Error: query_key not found in DashboardSearchStructures');
        }

        if (!this.loading_state_view_class) {
            throw new Error('Programmer Error: view_class not found in DashboardCardComponent');
        }

        // make a deep copy so we dont pollute the fresh templates
        if (this.query_key) {
            this.query_structure = $.extend(true, {}, DashboardSearchStructures[this.query_key]);
        } else if (!this.query_structure) {
            throw new Error('Programmer Error: query_structure must be passed in if not using query_key');
        }
        this.setup_query();

        // when our DashboardViewConfigurations structure tells us to fetch, we fetch
        this.listenTo(this.model.collection, 'dashboard_card:fetch', function (_options) {
            this_.fetch_data(_options);
        });
    },

    onRender: function (options) {
        if (this.no_query) {
            this.show_static_body_view();
            return;
        }
        this.show_loading_state();
    },

    /**
     * Instantiate the query model based on the query_key key
     */
    setup_query: function () {
        this.query_structure.metadata.company = { id: $f.active_company.id };

        var search_criterias = new SearchCriterias(this.query_structure);
        var dashboard_filters = new this.dashboard_filters_class();
        var extra_sc = this.get_additional_search_criterias();
        var account_sc = this.get_account_criteria();
        if (account_sc) {
            search_criterias.add(account_sc);
        }
        if (extra_sc && extra_sc.length) {
            search_criterias.add(extra_sc);
        }

        this.query_collection = new this.query_collection_class([], {
            search_criterias: search_criterias,
            dashboard_filters: dashboard_filters,
            global_search_options: this.get_group_search_options() // only group_id is added to the base json post
        });
    },

    /**
     * Queries the dashboard for the report data
     */
    fetch_data: function (options) {
        options = options || {};
        var this_ = this;

        // only fetch the data if we're in the active tab
        // * all tabs are rendered so we dont want to fetch as we scroll across *
        // note: some pages like tasks don't pass tab_id downward into the components so only if it exists
        if (this.tab_id && this.tab_id != $f.mobile.dashboard.active_tab_id) {
            return;
        }

        // when this is triggered from an intersection event
        if ((this.query_collection.is_fetched || this.query_collection.is_fetching) && options.from_viewport_scroll) {
            return;
        }

        this.r_down_state.reset();
        this.show_loading_state();

        this.query_collection.fetch({
            success: this.show_body_view.bind(this_, 'success'),
            error: this.show_body_view.bind(this_, 'error')
        });

        this.listenTo(this.query_collection, 'cache_updated', this.show_body_view.bind(this_, 'success'));
    },

    /**
     * Setup group specific search options to pass to the global_search_options
     * To be used only for group search options
     * @returns: {DashboardSearchOptions}
     */
    get_group_search_options: function () {
        if (this.group_or_account && $f.mobile.dashboard.is_group(this.group_or_account)) {
            return this.group_or_account.get_dashboard_search_options();
        }
        return null;
    },

    /**
     * Get implicit account search criteria from the group_or_account object
     */
    get_account_criteria: function () {
        if (this.group_or_account && $f.mobile.dashboard.is_account(this.group_or_account)) {
            return new SearchCriteria({
                field_id: 'account_id',
                operator_id: '=',
                value: this.group_or_account.id,
                display_value: this.group_or_account.get('name')
            });
        }
        return null;
    },

    /**
     * Return additional search criteria models <SearchCriteria>
     * Use this method to include search options for anything other than groups
     * @returns [List of SearchCriteria]
     */
    get_additional_search_criterias: function () {
        return [];
    },

    /**
     * Remove the loading section and either show down state or the body view
     *
     * @param state - string (error, success)
     * @param response - ajax response
     *
     */
    show_body_view: function (state, response) {
        // In case the user has left this dashboard view by the time our request has finished
        if (this.isClosed) {
            return;
        }

        if (this.r_loading_state) {
            this.r_loading_state.reset();
        }

        var zenput_code = response.zenput_code;
        if (state == 'error') {
            zenput_code = ZenputCodes.UNKNOWN_ERROR;
        }
        this.trigger('query_collection_loaded');

        if (zenput_code) {
            this.show_down_state(zenput_code);
            this.r_body.reset();
            return;
        }

        this.r_down_state.reset();
        this.r_body.show(
            new this.view_class({
                collection: response,
                query_collection: this.query_collection,
                parent_component: this
            })
        );
    },

    /**
     * Used for components that do not fetch data, ie, no groups downstate
     */
    show_static_body_view: function () {
        this.r_body.show(
            new this.view_class({
                parent_component: this
            })
        );
    },

    show_loading_state: function () {
        this.r_loading_state.show(new this.loading_state_view_class());
    },

    /**
     * Handle logic around zenput_code responses.
     * Overwrite in children if we need non-normal behavior
     *
     * @param zenput_code
     *
     * Renders r_down_state
     */
    show_down_state: function (zenput_code) {
        this.r_down_state.show(
            new ComponentDownState({
                zenput_code: zenput_code,
                query_key: this.query_key
            })
        );

        this.listenToOnce(this.r_down_state.currentView, 'downstate_retry', this.fetch_data);
    },

    /**
     * Handle click event from the main component.
     */
    on_content_click_event: function (event) {
        // Track dashboard card click/interaction
        if ($f.flags.get_flag('pendo_mobile_dashboard_tracking')) {
            pendoTrack('dashboard_card_clicked', {
                card_name: this.query_key,
                tab_id: this.tab_id
            });
        }

        this.on_content_click();
        return false;
    },

    /**
     * Method for sub-classes to override for click behavior
     */
    on_content_click: function () {
        return false;
    }
});

export const SectionHeader = Backbone.Marionette.Layout.extend({
    template: '#tpl_dashboard_section_header',

    templateHelpers: function () {
        return { title: this.view_options.title };
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
    }
});

// ** Implementation ** //

// ** Tabs ** //

export const TasksForLogbook = TasksPage.extend({
    is_top_level_page: false,
    search_options: null, // LogBookTaskSearchOptions,
    search_options_class: LogBookTaskSearchOptions,
    allowed_filters: {}, // empty = no filters
    show_search: false
});

export const DashboardTasksView = TasksPage.extend({
    toolbarnav_class: null,
    is_top_level_page: false,
    include_page_scrollable: false,
    tasks_collection_class: DashboardTasks,
    search_options_class: DashboardTaskSearchOptions,
    allowed_filters: {
        status: true,
        group_by: true,
        sort: true
    },

    initialize: function (options) {
        // Since we are not a top level page, we won't receive the task_bucket_collections as we would on the normal tasks page.
        // Instead, we have to reach into the nav and get it ourselves.
        var page_options = $f.mobile.nav.get_current_options();
        if (page_options.task_bucket_collections) {
            this.task_bucket_collections = page_options.task_bucket_collections;
        }

        DashboardTasksView.__super__.initialize.apply(this, arguments);
    },

    /**
     * On the dashboard we want to get the global active group or account and update the search options
     */
    _get_search_options: function (callback) {
        DashboardTasksView.__super__._get_search_options.call(this, function (search_options) {
            var group_or_account = $f.mobile.dashboard.get_active_account_or_group();

            search_options
                .findWhere({
                    field: $f.mobile.dashboard.is_account(group_or_account) ? 'account_id' : 'group_id'
                })
                .set({
                    value: group_or_account.id,
                    display_value: group_or_account.get('name')
                });

            // Clear out whichever search option is not active group or account
            search_options
                .findWhere({
                    field: $f.mobile.dashboard.is_account(group_or_account) ? 'group_id' : 'account_id'
                })
                .set({
                    value: null,
                    display_value: null
                });

            // remove any creator/assignee settings
            search_options.findWhere({ field: 'filter' }).set({
                value: 'all',
                display_value: null
            });

            callback(search_options);
        });
    }
});

export const DashboardTabTasks = BaseDashboardTab.extend({
    onRender: function () {
        // In case the user has left this dashboard view by the time our request has finished
        if (this.isClosed) {
            return;
        }
    },

    on_tab_focused: function () {
        this.fetch_and_render_tasks();
    },

    fetch_and_render_tasks: function () {
        if (!this.r_body) {
            return;
        }

        this.r_body.show(
            new DashboardTasksView({
                dashboard_cards: this.dashboard_cards
            })
        );
    },

    setup_cards: function () {
        // no cards
    }
});

export const DashboardTabNoGroups = BaseDashboardTab.extend({
    setup_cards: function () {
        this.dashboard_cards = new DashboardViewConfigurations([
            {
                view_class: DashboardCardNoGroupsDownstate
            }
        ]);
    }
});

export const DashboardTabTeam = BaseDashboardTab.extend({
    clear_group_or_account_filter: function () {
        if ($f.mobile.dashboard.active_search_item_name) {
            $f.mobile.dashboard.active_search_item_name = null;
            $f.mobile.nav.show(MobileDashboard, { is_top_level_page: true });
        }
    },

    setup_cards: function () {
        var this_ = this;
        // this will always be a group since we don't show this tab if it's an account
        // but since the subcomponents can expect either, we continue the naming here with the keys we pass into the
        // view options
        var active_group = $f.mobile.dashboard.get_active_account_or_group();

        // if they're a multi account owner, just show them 'my tasks' and the accounts that they own in this team tab.
        if ($f.mobile.dashboard.is_multi_account_owner()) {
            var owned_accounts = $f.mobile.dashboard.accessible_accounts.get_owned_accounts_for_active_user();
            this.dashboard_cards = new DashboardViewConfigurations([
                {
                    view_class: DashboardCardMyTasks
                },
                {
                    view_class: SectionHeader,
                    view_options: { title: 'Locations' }
                }
            ]);

            owned_accounts.forEach(function (account) {
                this_.dashboard_cards.add({
                    view_class: DashboardCardCurrentTasks,
                    view_options: {
                        group_or_account: account
                    }
                });
            });
            return;
        }

        this.dashboard_cards = new DashboardViewConfigurations([
            {
                view_class: DashboardCardMyTasks
            },
            {
                view_class: DashboardCardCurrentTasks,
                view_options: {
                    group_or_account: active_group,
                    include_legend: true
                }
            }
        ]);

        var child_groups = active_group.get('child_distribute_groups') || [];
        if (child_groups.length > 0) {
            this.dashboard_cards.add({
                view_class: SectionHeader,
                view_options: { title: 'Teams' }
            });
            child_groups.each(function (child_group) {
                this_.dashboard_cards.add({
                    view_class: DashboardCardCurrentTasks,
                    view_options: {
                        group_or_account: child_group,
                        is_sub_group: true,
                        show_component_header: true
                    }
                });
            });
        }

        // only show accounts if this is a leaf node of the hierarchy
        if (!active_group.get('is_leaf')) {
            return;
        }

        // Now add accounts associated with this distribute group as well
        var accounts = new AccountsForDistributeGroup({
            distribute_group_id: active_group.id
        });
        accounts.fetch({
            success: function (acct_collection) {
                if (!acct_collection || acct_collection.length == 0) {
                    return;
                }

                this_.dashboard_cards.add({
                    view_class: SectionHeader,
                    view_options: { title: 'Locations' }
                });

                acct_collection.forEach(function (account) {
                    this_.dashboard_cards.add({
                        view_class: DashboardCardCurrentTasks,
                        view_options: {
                            group_or_account: account,
                            show_component_header: true,
                            is_sub_group: true
                        }
                    });
                });
            }
        });
    }
});

export const DashboardTabSummary = BaseDashboardTab.extend({
    setup_cards: function () {
        var group_or_account = $f.mobile.dashboard.get_active_account_or_group();
        this.dashboard_cards = new DashboardViewConfigurations([]);
        this.dashboard_cards.add([
            {
                view_class: DashboardCardTasksCurrentGroup,
                view_options: {
                    group_or_account: group_or_account
                }
            }
        ]);

        this.dashboard_cards.add({
            view_class: DashboardCardTaskCompletionRate,
            view_options: { group_or_account: group_or_account }
        });

        if ($f.mobile.dashboard.is_group(group_or_account)) {
            this.dashboard_cards.add({
                view_class: DashboardCardOngoingProjects,
                view_options: { group_or_account: group_or_account }
            });
        }

        this.dashboard_cards.add([
            {
                view_class: DashboardCardRecurringProjects,
                view_options: { group_or_account: group_or_account }
            },
            {
                view_class: MobileDashboardCardTopTriggeredWorkflows,
                view_options: { group_or_account: group_or_account }
            },
            {
                view_class: MobileDashboardCardFormsTopNos,
                view_options: { group_or_account: group_or_account }
            }
        ]);
    }
});

export const DashboardTabLogbook = BaseDashboardTab.extend({
    initialize: function () {
        DashboardTabLogbook.__super__.initialize.apply(this, arguments);
    },

    setup_cards: function () {
        var group_or_account = $f.mobile.dashboard.get_active_account_or_group();

        this.dashboard_cards = new DashboardViewConfigurations([
            {
                view_class: DashboardCardLogbook,
                view_options: { group_or_account: group_or_account, frequency: $f.utils.recurring.frequencies.DAILY }
            },
            {
                view_class: DashboardCardLogbook,
                view_options: { group_or_account: group_or_account, frequency: $f.utils.recurring.frequencies.WEEKLY }
            },
            {
                view_class: DashboardCardLogbook,
                view_options: { group_or_account: group_or_account, frequency: $f.utils.recurring.frequencies.MONTHLY }
            }
        ]);
    }
});

export const MobileTaskStatus = Backbone.Marionette.ItemView.extend({
    template: '#tpl_tasks_stat',
    color: null, // color based on task due type
    className: 'task_link',
    title: '',

    templateHelpers: function () {
        return { title: this.title || '' };
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
    },

    onRender: function () {
        this.$el.addClass(this.color);
    }
});

/**
 * Google Maps has an issue drawing a map in an element that was not previously
 * rendered on the page for some messed up reason.  To fix this, we draw right
 * in the layout rather than this have its own template.  Yes, it sucks
 */
export const AccountLocationMap = Backbone.Marionette.ItemView.extend({
    template: '#tpl_dashboard_info_map_panel',
    className: 'account_location',
    model: null, // Account - contains the actual location of the company for which submissions are being viewed
    map_options: null, // can't fill out options here yet because the google.maps lib may not have been loaded
    render_map: true, // if there are no submissions or pins on the map, we won't show the Google Map

    // @bruce: I have no idea why, but for some reason at this part of the DOM multiple click events
    // and mouse up events are being generated. on Surface, but not on other platforms.
    // Pointerup does work correctly, however, on Surface so switching to that when on Windows
    events: isWindows() ? { 'pointerup .google_map': 'open_map' } : { 'click .google_map': 'open_map' },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        // set up map options
        this.map_options = {
            zoom: 15,
            center: new google.maps.LatLng(39.82, -98.5795),
            mapTypeId: google.maps.MapTypeId.ROADMAP,
            disableDefaultUI: true,
            streetViewControl: false,
            zoomControl: false,
            scrollwheel: false,
            draggable: false,
            clickable: false,
            disableDoubleClickZoom: true
        };
    },

    open_map: function (e) {
        $f.mobile.open_uri(this.model.get_latlon_link());
        return false;
    },

    onShow: function () {
        if (!this.model.get('lat') || !this.model.get('lon')) {
            return;
        }

        // Generate a map, pass in options.
        var map = new google.maps.Map(this.$el.find('.google_map').get(0), this.map_options);
        var bounds = new google.maps.LatLngBounds();

        if (this.model.get('lat') && this.model.get('lon')) {
            bounds.extend(new google.maps.LatLng(this.model.get('lat'), this.model.get('lon')));
        }

        map.fitBounds(bounds);

        // need to wait for above fitBouds() to finish before we can set the size
        google.maps.event.addListener(map, 'bounds_changed', function (event) {
            google.maps.event.trigger(map, 'resize');
            if (this.getZoom() !== 16) {
                this.setZoom(16);
            }
        });
    }
});

export const InfoStore = Backbone.Marionette.ItemView.extend({
    template: '#tpl_dashboard_info_store',
    model: null,

    templateHelpers: function () {
        return {
            map_link: this.model.get_latlon_link()
        };
    }
});

export const InfoOwnerGroup = Backbone.Marionette.ItemView.extend({
    template: '#tpl_info_owner_group',
    model: null,
    account_owners: null,
    className: 'info_owner_group',

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
    },

    templateHelpers: function () {
        var this_ = this;

        var owners = this.account_owners.filter(function (owner) {
            return this_.model.id === owner.get('user_role_id');
        });

        // Some old users have no display name so lets make it here
        var _get_owner_name = function (owner) {
            return owner.get('display_name')
                ? owner.get('display_name')
                : $f.utils.generate_display_name(owner.get('first_name'), owner.get('last_name'));
        };

        var owner_names = '';
        _.each(owners, function (owner, index) {
            if (index === owners.length - 1) {
                owner_names += _get_owner_name(owner);
                return;
            }

            owner_names += _get_owner_name(owner) + ', ';
        });

        return {
            owner_names: owner_names
        };
    }
});

export const InfoOwnerGroups = Backbone.Marionette.CollectionView.extend({
    itemView: InfoOwnerGroup,
    collection: null, // UserRoles
    account_owners: null,

    itemViewOptions: function () {
        return {
            account_owners: this.account_owners
        };
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        if (!this.account_owners) {
            return false;
        }

        // Check if we have owners without roles and add a new model to collection
        var has_owners_without_roles = this.account_owners.filter(function (owner) {
            return !owner.get('user_role_id');
        });

        if (has_owners_without_roles) {
            // We need to specify we want to add this to the end of the collection
            this.collection.add(
                {
                    name: 'Owners without Role',
                    id: null
                },
                { at: this.collection.length }
            );
        }
    },

    /**
     * We will not show user_roles with no account owner
     */
    appendHtml: function (collectionView, itemView, index) {
        if (this.account_owners && !this.account_owners.findWhere({ user_role_id: itemView.model.id })) {
            return;
        }

        return InfoOwnerGroups.__super__.appendHtml.apply(this, arguments);
    },

    onRender: function () {
        var this_ = this;
        if (this.children.first() instanceof EmptyListMessage) {
            this.listenTo(this.children.first(), 'empty_view:retry_fetch', function () {
                this_.trigger('empty_view:retry_fetch');
            });
        }
    },

    getEmptyView: function () {
        return EmptyListMessage.extend({
            zenput_code: this.zenput_code,
            allow_retry: true
        });
    }
});

export const SensorTemperatureLogReport = BaseToolbarNavPageLayout.extend({
    template: '#tpl_sensor_temp_log_report',
    allow_reverse_transitions: true,
    toolbarnav_show_add_task: false,
    toolbarnav_title: function () {
        return _t('mobile_compliance_sensor_temperature_log_report');
    },
    model: null, // ExportSensorTempLogReport

    regions: {
        r_info_email_selector: '.r_info_email_selector'
    },

    events: {
        'click .submit_sensor_temp_log_report': 'exportReport',
        'change select': 'handleSelectDate'
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        if (!this.model) {
            const location = $f.mobile.dashboard.get_active_account_or_group();

            this.model = new ExportSensorTempLogReport({
                location_id: location.get('id'),
                recipient_email: location.get('email'),
                date_start: 'date_week_last'
            });
        }

        this.handleInvalidEmail = this.handleInvalidEmail.bind(this);
        this.handleValidEmail = this.handleValidEmail.bind(this);
        this.setRecipientEmail = this.setRecipientEmail.bind(this);
    },

    onRender: function () {
        this.renderEmailSelector();
    },

    exportReport: function (event) {
        event.preventDefault();
        showLoadingDialog();

        this.model.fetch({
            success: function (response) {
                hideLoadingDialog();
                showSuccessMessage();
            },
            error: function (response) {
                hideLoadingDialog();
                showErrorMessage();
            }
        });
    },

    handleSelectDate: function (event) {
        const target = $(event.target);
        this.model.set('date_start', target.val());
    },

    renderEmailSelector: function () {
        const location = $f.mobile.dashboard.get_active_account_or_group();
        const user = $f.active_user;

        this.r_info_email_selector.show(
            new RecipientEmailSelector({
                title: _t('mobile_compliance_sensor_temperature_log_email_title'),
                showLegend: false,
                userEmail: user.get('email'),
                locationEmail: location.get('email'),
                setLocationEmailByDefault: true,
                saveEmailAddress: this.setRecipientEmail,
                handleValidEmail: this.handleValidEmail,
                handleInvalidEmail: this.handleInvalidEmail,
                handleShowLegend: null
            })
        );
    },

    setRecipientEmail: function (emailAddress) {
        this.model.set('recipient_email', emailAddress);
    },

    handleValidEmail: function () {
        const submitButton = this.$('.submit_sensor_temp_log_report');
        submitButton.prop('disabled', false);
        submitButton.removeClass('disabled');
    },

    handleInvalidEmail: function () {
        const submitButton = this.$('.submit_sensor_temp_log_report');
        submitButton.prop('disabled', true);
        submitButton.addClass('disabled');
    }
});

export const ComplianceTab = BaseTabbedLayoutPage.extend({
    template: '#tpl_compliance_tab',
    className: 'tabbed_layout_page mobile_dashboard_page',

    events: {
        'click #submission_report': 'showSubmissionReport',
        'click #sensor_temp_log_report': 'showTempLogReport'
    },

    showSubmissionReport: function (event) {
        if (!isConnected()) {
            showOfflineMessage();
            return false;
        }

        $f.mobile.nav.show(ComplianceProjectCompletionRateReport, { use_transitions: true });

        return false;
    },

    showTempLogReport: function (event) {
        if (!isConnected()) {
            showOfflineMessage();
            return false;
        }

        $f.mobile.nav.show(SensorTemperatureLogReport, { use_transitions: true });
        return false;
    }
});

export const DashboardTabInfo = BaseTabbedLayoutPage.extend({
    template: '#tpl_dashboard_info',
    className: 'tabbed_layout_page mobile_dashboard_page info_container card',
    account: null,
    current_user_id: null,
    submissions: null,

    regions: {
        r_info_map: '.r_info_map',
        r_info_store: '.r_info_store',
        r_info_owners: '.r_info_owners'
    },

    initialize: function (options) {
        DashboardTabInfo.__super__.initialize.apply(this, arguments);
        this.account = $f.mobile.dashboard.get_active_account_or_group();
    },

    on_tab_focused: function () {
        this._render_map();
        this._render_store_info();
        this.fetch_and_render_account_info();
    },

    fetch_and_render_account_info: function () {
        var this_ = this;
        showLoadingDialog();
        var user_roles = new MobileUserRoles();

        var fetch_error = function () {
            hideLoadingDialog();

            if (!this_.r_info_owners) {
                return;
            }

            this_.r_info_owners.show(
                new InfoOwnerGroups({
                    collection: user_roles,
                    zenput_code: ZenputCodes.UNKNOWN_ERROR
                })
            );

            this_.listenTo(
                this_.r_info_owners.currentView,
                'empty_view:retry_fetch',
                this_.fetch_and_render_account_info
            );
        };

        this.account.fetch({
            success: function (account) {
                user_roles.fetch({
                    success: function () {
                        hideLoadingDialog();
                        this_.r_info_owners.show(
                            new InfoOwnerGroups({
                                collection: user_roles,
                                account_owners: this_.account.get('owners')
                            })
                        );
                    },
                    error: function () {
                        return fetch_error();
                    }
                });
            },
            error: function () {
                return fetch_error();
            }
        });
    },

    _render_map: function (options) {
        var this_ = this;

        if (!this.account.get('lat') || !this.account.get('lon')) {
            return;
        }

        setTimeout(function () {
            // let's wait a little bit for any animations to finish
            $f.mobile.load_google_maps_api({
                success: function () {
                    if (!this_.r_info_map) {
                        return;
                    } // just in case user has left by time of error and view has close()d
                    this_.r_info_map.show(
                        new AccountLocationMap({
                            model: this_.account
                        })
                    );
                },
                error: function () {
                    hideLoadingDialog();
                    showOfflineMessage();
                }
            });
        }, 300);
    },

    _render_store_info: function () {
        this.r_info_store.show(
            new InfoStore({
                model: this.account
            })
        );
    }
});

export const GalleryImagesList = TitleImages.extend({
    collection: null, // GalleryImages
    zenput_code: null, // Error codes for fetch
    allow_retry: null,

    itemViewOptions: {
        isGallery: true
    },

    className: 'thumbnails_location_gallery',

    getEmptyView: function () {
        return EmptyListMessage.extend({
            image_filename: 'gallery_downstate.svg',
            icon_class: null,
            i18n_key_message: 'mobile_empty_list_message_no_photos',
            zenput_code: this.zenput_code,
            allow_retry: this.allow_retry
        });
    },

    onRender: function () {
        if (this.collection.length) {
            this.$el.removeClass('empty_gallery');
        } else {
            this.$el.addClass('empty_gallery');
        }
    }
});

export const DashboardTabGallery = BaseTabbedLayoutPage.extend({
    template: '#tpl_dashboard_gallery',
    collection: null, // GalleryImages
    account: null, // Account
    _last_fetch_timestamp: 0,

    regions: {
        r_gallery_images: '.r_gallery_images'
    },

    onShow: function () {
        this._setup_infinite_scroll();
    },

    on_tab_focused: function () {
        this._render_gallery_images();
    },

    _render_gallery_images: function () {
        var this_ = this;

        this.account = $f.mobile.dashboard.get_active_account_or_group();
        this.collection = new GalleryImages([], {
            account: this_.account
        });

        showLoadingDialog();

        this.collection.fetch({
            success: function () {
                hideLoadingDialog();
                this_.r_gallery_images.show(
                    new GalleryImagesList({
                        collection: this_.collection
                    })
                );
            },
            error: function () {
                hideLoadingDialog();
                this_.r_gallery_images.show(
                    new GalleryImagesList({
                        collection: this_.collection
                    })
                );
            }
        });
    },

    _remove_loading_spinner: function () {
        this.$el.parent().siblings('.infinite_scroll_loading').css('visibility', 'hidden');
    },

    _perform_scroll_fetch: function () {
        var this_ = this;
        var collection_paginator = this_.collection.paginator;

        if (!collection_paginator) {
            $f.mobile.logging.log_error(
                'Attempting to use infinite scroll with collection that does not have a paginator'
            );
            return;
        }

        collection_paginator.set('start', collection_paginator.get('start') + collection_paginator.get('limit'));
        this.$el.parent().siblings('.infinite_scroll_loading').css('visibility', 'visible');

        this.collection.fetch({
            force: true,
            success: function () {
                this_._remove_loading_spinner();
            },
            error: function () {
                this_._remove_loading_spinner();
            },
            remove: false
        });
    },

    _setup_infinite_scroll: function () {
        var this_ = this;
        var $pg_scrollable = this.$el.find('.page_scrollable');

        setTimeout(function () {
            $pg_scrollable.scrollTop(0); // when the page inits, be sure to jump to the top.  inifite scroll will keep going otherwise
            $pg_scrollable.off('scroll');
            $pg_scrollable.on('scroll', function () {
                if ($pg_scrollable.scrollTop() < $pg_scrollable.get(0).scrollHeight - $pg_scrollable.height() - 100) {
                    return;
                }

                // limit fetches to once every 2 seconds
                if (this_._last_fetch_timestamp + 1000 > new Date().getTime()) {
                    return;
                }
                this_._last_fetch_timestamp = new Date().getTime();

                if (!isConnected()) {
                    return;
                }

                this_._perform_scroll_fetch();
            });
        }, 100);
    }
});

/**
 * An item for AccountSubmissionsList submission list.
 */
export const AccountSubmissionsItem = HistoryListItem.extend({
    template: '#tpl_account_submissions_item'
});

/**
 * A list view with the submissions for an account.
 *
 * Note, this does not extend from BaseSubmissionsHistoryList like the others,
 * since AccountSubmissions is not a BaseToolbarnavPage but just a list inside one.
 */
export const AccountSubmissionsList = Backbone.Marionette.CompositeView.extend({
    tagName: 'div',
    template: '#tpl_account_submissions',
    itemView: AccountSubmissionsItem,
    itemViewContainer: '.account_submissions_history_list',
    model: null, // Account
    collection: null, // AccountSubmissions
    zenput_code: null, // Error codes for fetch
    allow_retry: null,

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
    },

    onRender: function () {
        var this_ = this;
        // Check if our children view is an empty list and setup a listen to allow for refetching
        if (this.children.first() instanceof EmptyListMessage) {
            this.listenTo(this.children.first(), 'empty_view:retry_fetch', function () {
                this_.trigger('empty_view:retry_fetch');
            });
        }
    },

    getEmptyView: function () {
        return EmptyListMessage.extend({
            image_filename: 'feed_downstate.svg',
            icon_class: null,
            i18n_key_message: 'mobile_empty_list_message_no_activity',
            zenput_code: this.zenput_code,
            allow_retry: this.allow_retry
        });
    }
});

export const DashboardTabFeed = BaseTabbedLayoutPage.extend({
    template: '#tpl_dashboard_feed',
    className: 'tabbed_layout_page mobile_dashboard_page info_container card',
    model: null, // TabbedLayoutPageListItem
    account: null,

    regions: {
        r_feed_submissions: '.r_feed_submissions'
    },

    initialize: function (options) {
        DashboardTabFeed.__super__.initialize.apply(this, arguments);

        this.account = $f.mobile.dashboard.get_active_account_or_group();
    },

    on_tab_focused: function () {
        this.fetch_and_render_submission_feed();
    },

    fetch_and_render_submission_feed: function () {
        var this_ = this;

        var submissions = new AccountSubmissions([], { account: this.account });

        showLoadingDialog();
        submissions.fetch({
            success: function () {
                hideLoadingDialog();

                if (!this_.r_feed_submissions) {
                    return;
                }

                this_.r_feed_submissions.show(
                    new AccountSubmissionsList({
                        collection: submissions
                    })
                );
            },
            error: function () {
                hideLoadingDialog();

                this_.r_feed_submissions.show(
                    new AccountSubmissionsList({
                        collection: submissions,
                        zenput_code: ZenputCodes.UNKNOWN_ERROR,
                        allow_retry: true
                    })
                );

                this_.listenTo(this_.r_feed_submissions.currentView, 'empty_view:retry_fetch', function () {
                    this_.fetch_and_render_submission_feed();
                });
            }
        });
    }
});

// ** Components and ComponentBodyViews ** //

export const ComponentBodyViewNoGroups = Backbone.Marionette.ItemView.extend({
    template: '#tpl_dashboard_component_user_not_in_groups',
    className: 'no_teams_downstate'
});

export const ComponentNoGroupsDownState = DashboardCardComponent.extend({
    view_class: ComponentBodyViewNoGroups,
    no_query: true
});

export const ComponentBodyViewTasks = Backbone.Marionette.Layout.extend({
    template: '#tpl_component_body_view_tasks',
    className: 'tasks_team_due',

    regions: {
        r_tasks_bar: '.tasks_team_pie',
        r_tasks_overdue: '.tasks_team_overdue',
        r_tasks_due_today: '.tasks_team_due_today',
        r_tasks_due_week: '.tasks_team_due_week',
        r_tasks_due_month: '.tasks_team_due_month'
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
    },

    templateHelpers: function () {
        return {
            include_current_task_title: this.parent_component.show_component_header || false
        };
    },

    onRender: function () {
        var overdue = this.collection.get('tasks_team_overdue');
        var due_today = this.collection.get('tasks_team_due_today');
        var due_this_week = this.collection.get('tasks_team_due_this_week');
        var total = this.collection.get('tasks_team_total');

        this.r_tasks_overdue.show(
            new MobileTaskStatus({
                model: this.collection.get('tasks_team_overdue'),
                color: overdue.get('count') === 0 ? 'hidden' : 'overdue',
                title: _t('mobile_tasks_toolbarnav_header_overdue'),
                start_date: 'reset',
                end_date: overdue.get('date_end').$date
            })
        );
        this.r_tasks_due_today.show(
            new MobileTaskStatus({
                model: this.collection.get('tasks_team_due_today'),
                color: due_today.get('count') === 0 ? 'no_due_green' : 'due_today',
                title: _t('mobile_tasks_toolbarnav_header_today'),
                start_date: moment(due_today.get('date_start')).startOf('day').valueOf(),
                end_date: moment(due_today.get('date_end')).endOf('day').valueOf()
            })
        );
        this.r_tasks_due_week.show(
            new MobileTaskStatus({
                model: this.collection.get('tasks_team_due_this_week'),
                color: due_this_week.get('count') === 0 ? 'no_due_green' : 'due_week',
                title: _t('mobile_tasks_toolbarnav_header_this_week'),
                start_date: due_this_week.get('date_start').$date,
                end_date: due_this_week.get('date_end').$date
            })
        );
        this.r_tasks_due_month.show(
            new MobileTaskStatus({
                model: this.collection.get('tasks_team_total'),
                color: total.get('count') === 0 ? 'no_due_green' : 'due_month',
                title: _t('mobile_dashboard_card_task_stats_total'),
                start_date: 'reset',
                end_date: 'reset'
            })
        );
    }
});

export const ComponentTasks = DashboardCardComponent.extend({
    query_key: 'tasks_current',
    allow_click_header: true,
    view_class: ComponentBodyViewTasks,
    loading_state_view_class: ComponentLoadingStateTasks,
    query_collection_class: DashboardCardResultListForTasksCurrent,

    // We are going to catch the zenput_code returned when we get no tasks back and instead of showing a down state
    // we're going to show all 0s for the tasks, which is more appropriate for no tasks returned from the backend.
    show_down_state: function (zenput_code) {
        if (zenput_code != ZenputCodes.NO_DATA_IN_RANGE) {
            return ComponentTasks.__super__.show_down_state.apply(this, arguments);
        }

        var mock_data = [
            {
                count: 0,
                id: 'tasks_team_due_today',
                name: 'Due Today',
                date_end: { $date: 1510127999000 },
                date_start: { $date: 1510127999000 }
            },
            {
                count: 0,
                id: 'tasks_team_due_this_week',
                name: 'Due This Week',
                date_end: { $date: 1510127999000 },
                date_start: { $date: 1510127999000 }
            },
            {
                count: 0,
                id: 'tasks_team_total',
                name: 'Total',
                date_end: { $date: 1510127999000 },
                date_start: { $date: 1510127999000 }
            },
            {
                count: 0,
                id: 'tasks_team_overdue',
                name: 'Overdue',
                date_end: { $date: 1510127999000 },
                date_start: { $date: 1510127999000 }
            }
        ];
        var mock_collection = new BaseZenputCollection(mock_data);
        this.r_down_state.show(
            new ComponentBodyViewTasks({
                collection: mock_collection,
                parent_component: this
            })
        );
    },

    on_content_click: function () {
        if (this.disable_content_click) {
            return false;
        }

        if (this.group_or_account === $f.mobile.dashboard._active_account_or_group) {
            $f.mobile.dashboard.active_tab_id = 'tasks';
            $f.application.vent.trigger('tabbed_layout:jump_to_page', $f.mobile.dashboard.active_tab_id);
            return;
        }
        $f.mobile.nav.show(MobileDashboard, {
            active_account_or_group: this.group_or_account,
            active_tab_id: 'tasks'
        });
    }
});

export const ComponentMyTasks = ComponentTasks.extend({
    query_key: 'tasks_current',
    view_class: ComponentBodyViewTasks,

    // we override events entirely as having the same behavior for body & down_state is unique to this component
    events: {
        'click .dashboard_card_component_body': 'navigate_to_my_tasks',
        'click .dashboard_card_component_down_state': 'navigate_to_my_tasks'
    },

    get_additional_search_criterias: function () {
        return [
            new SearchCriteria({
                field_id: 'assignee_user_id',
                operator_id: '=',
                value: $f.active_user.id
            })
        ];
    },

    navigate_to_my_tasks: function () {
        if (this.tab_id !== 'team') {
            return false;
        }
        // creating new search options and overriding cache to get default task list always on click-through
        var search_options = new TaskSearchOptions();
        search_options.save_local(function () {
            $f.mobile.nav.show(TasksPage, { is_top_level_page: true });
        });
    }
});

export const ComponentBodyViewTaskCompletionRate = Backbone.Marionette.Layout.extend({
    template: '#tpl_component_body_view_task_completion_rate',

    categories: null,
    series: null,
    comparisons: null,
    chart_options: null,
    $bounding_elem: null,
    chart: null,

    templateHelpers: function () {
        return {
            include_legend: this.parent_component.include_legend,
            title: _t('mobile_dashboard_tcr_graph_name')
        };
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        this.series = this.collection.get('series').get('series_data');
        this.categories = this.collection.get('categories').get('data');

        if (!this.categories || !this.series) {
            throw new Error('ERROR: categories or series are missing.');
        }

        $(window).on('resize', this.on_resize.bind(this));
    },

    // DEM: copy of dashboards/views.js:617: $f.dashboards.views.TasksCompletionRateColumnChart
    onRender: function () {
        this.$bounding_elem = this.$el.find('.column_chart');
        this.setup_chart_options();
        this.show_charts();
    },

    onShow: function () {
        this.on_resize();
    },

    on_resize: function () {
        var this_ = this;

        if (this.chart) {
            // We need to wait a bit for the resize to occur before resetting the chart size
            setTimeout(function () {
                this_.chart.setSize(
                    this_.$el.find('.right_pane').width(),
                    this_.$el.find('.right_pane').height(),
                    false
                );

                // Using opacity here because we need the pane to have a height and width
                this_.$el.find('.column_chart').css('opacity', 1);
            }, 500);
        }
    },

    setup_chart_options: function () {
        var this_ = this;

        // Assign colors in this order regardless of having one or two or however many series for this graph
        var colors = ['#67d69c', '#5c697c']; // Want to add more colors? go ahead!
        for (var i = 0; i < this_.series.length; i++) {
            this_.series[i].color = colors[i % colors.length];
        }

        var height = 100;

        this.chart_options = {
            chart: {
                type: 'column',
                marginTop: 20,
                height: height,
                marginBottom: 20
            },
            title: {
                text: ''
            },
            subtitle: {
                enabled: false
            },
            xAxis: {
                categories: this.categories,
                tickLength: 0
            },
            yAxis: {
                min: 0,
                max: 100,
                tickPositioner: function () {
                    return [0, 20, 40, 60, 80, 100];
                },
                lineColor: '#F2F7FA',
                title: {
                    enabled: false
                },
                labels: {
                    enabled: false
                }
            },
            legend: {
                enabled: false
            },
            tooltip: {
                enabled: false
            },
            plotOptions: {
                column: {
                    pointPadding: 0,
                    borderWidth: 0,
                    enableMouseTracking: false,
                    hover: {
                        enabled: false
                    }
                },
                series: {
                    animation: false
                }
            },
            credits: {
                enabled: false
            },
            series: this_.series
        };
    },

    show_charts: function () {
        this.$bounding_elem.highcharts('Chart', this.chart_options);
        this.chart = this.$bounding_elem.highcharts();
    },

    onClose: function () {
        $(window).off('resize');
    }
});

export const ComponentTaskCompletionRate = DashboardCardComponent.extend({
    query_key: 'tasks_completion_rate',
    view_class: ComponentBodyViewTaskCompletionRate,
    loading_state_view_class: ComponentLoadingStateTasksCompletionRate,

    on_content_click: function () {
        // This is intentionally going to summary tab when you click on the task chiclets.
        if (this.group_or_account === $f.mobile.dashboard._active_account_or_group) {
            $f.mobile.dashboard.active_tab_id = 'summary';
            $f.application.vent.trigger('tabbed_layout:jump_to_page', $f.mobile.dashboard.active_tab_id);
            return;
        }

        $f.mobile.nav.show(MobileDashboard, {
            active_account_or_group: this.group_or_account,
            active_tab_id: 'summary'
        });
    }
});

/**
 * An individual column header for the BaseTopItemsHeaderList.
 */
export const MobileBaseTopItemsHeaderListItem = Backbone.Marionette.ItemView.extend({
    template: '#tpl_base_top_items_header_list_item',
    tagName: 'th',

    model: null, // TopItemHeader
    card_collection: null, // MobileBaseDashboardCardResultList

    templateHelpers: function () {
        var freq = this.card_collection.search_criterias.findWhere({ field_id: 'recurring_frequency' }).get('value');

        var title_date = moment(this.model.get('label'));

        var title = null;
        var subtitle = null;

        if (freq === $f.utils.recurring.frequencies.DAILY) {
            title = title_date.format('M/D');
            subtitle = title_date.format('ddd');
        } else if (freq === $f.utils.recurring.frequencies.WEEKLY) {
            title = title_date.format('M/D');
            subtitle = _t('mobile_dashboard_top_item_header_week_of');
        } else {
            // MONTHLY
            title = title_date.format('MMM');
        }

        return {
            title: title,
            subtitle: subtitle,
            is_today: title_date.isSame(new Date(), 'day')
        };
    },

    initialize: function (options) {
        var this_ = this;

        options = options || {};
        $.extend(this, options);

        if (this.card_collection.selected_header) {
            this.listenTo(this.card_collection, 'change', function () {
                this_.render();
            });
        }
    },

    onRender: function () {
        this.$el.attr('class', this.is_selected_header());
    },

    is_selected_header: function () {
        var className_ = 'top_items_card_header';
        if (this.card_collection.selected_header) {
            // @author:david @author:dem we cannot compare IDs because header IDs are not consistent across paginations
            // (e.g., IDs from [0,20], [20-40] do not match [0-40])
            return this.model.get('sort_key') === this.card_collection.selected_header.get('sort_key')
                ? (className_ += ' selected')
                : className_;
        }

        return className_;
    }
});

export const MobileBaseTopItemsHeaderList = Backbone.Marionette.CollectionView.extend({
    tagName: 'tr',
    itemView: MobileBaseTopItemsHeaderListItem,

    collection: null, // TopItemHeaders
    card_collection: null, // MobileBaseDashboardCardResultList

    events: {
        'click .top_items_card_previous a': 'scroll_left',
        'click .top_items_card_next a': 'scroll_right'
    },

    appendHtml: function (collectionView, itemView, index) {
        // Adding an empty header cell here. This is the header for the text items in the card. This column does not
        // have any header but needs to exist for the other cells to line up properly
        if (!this.$el.find('.empty_header_item').length) {
            this.$el.html('<th class="empty_header_item"></th>');
        }

        return MobileBaseTopItemsHeaderList.__super__.appendHtml.apply(this, arguments);
    },

    itemViewOptions: function () {
        return {
            card_collection: this.card_collection
        };
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
    }
});

export const ComponentBodyViewTopItemsRow = Backbone.Marionette.ItemView.extend({
    template: '#tpl_component_bodyview_top_items_row',
    tagName: 'tr',

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
    },

    templateHelpers: function () {
        var column = this.model.get('columns')[0];

        return {
            column: column,
            value: column.value || column.value == 0 ? column.value.toFixed() : ''
        };
    }
});

/**
 * Some of this is modeled after $f.web.views.BaseZenputTable on web (@see this.show_header()). In the future,
 * we may want to break the table functionality out into its own base table for mobile if we start to show
 * tables elsewhere.
 */
export const ComponentBodyViewTopItems = Backbone.Marionette.CompositeView.extend({
    template: '#tpl_component_bodyview_top_items',
    itemView: ComponentBodyViewTopItemsRow,
    itemViewContainer: 'tbody',
    show_default_four_weeks_header: true,

    _order_by_view: null,

    templateHelpers: function () {
        var helpers = { header_title: null };

        if (!this.show_default_four_weeks_header) {
            return helpers;
        }

        // @author:david if this gets more complicated, we could use show_header() with a proper
        // TopItemHeaders, but for now this is fine.
        return $.extend(helpers, {
            header_title: _t('mobile_dashboard_tcr_graph_query_timerange_desc')
        });
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
    },

    /**
     * Shows a provided view as the header of the table. Automatically sets this._order_by_view
     *
     * @author:david this was copied over from $f.web.views.BaseZenputTable. If end up having more tables on mobile,
     * we should make a similar base table for all of mobile with this in it.
     *
     * @param  {Backbone.Marionette.View} view The header view to show
     */
    show_header: function (view) {
        if (this._order_by_view) {
            this._order_by_view.close();
        }
        this._order_by_view = view;

        this.$el.find('thead').html(view.el);
        view.render();
        if (view.onShow) {
            view.onShow();
        }
    }
});

/* eslint-disable-next-line max-len */
export const ComponentBodyViewTopTriggeredWorkflow = ComponentBodyViewTopItemsRow.extend({
    template: '#tpl_component_bodyview_top_triggered_row',

    templateHelpers: function () {
        var info = this.model.get('info');

        var spr_helpers = ComponentBodyViewTopTriggeredWorkflow.__super__.templateHelpers;
        return $.extend(spr_helpers.apply(this, arguments), {
            saved_search_title: info.saved_search ? info.saved_search.title : '',
            form_title: info.form_template ? info.form_template.title : ''
        });
    }
});

/* eslint-disable-next-line max-len */
export const ComponentBodyViewTopTriggeredWorkflows = ComponentBodyViewTopItems.extend({
    show_default_four_weeks_header: true,
    itemView: ComponentBodyViewTopTriggeredWorkflow
});

/* eslint-disable-next-line max-len */
export const ComponentTopTriggeredWorkflows = DashboardCardComponent.extend({
    query_key: 'workflows_top_triggered',
    view_class: ComponentBodyViewTopTriggeredWorkflows,
    loading_state_view_class: ComponentLoadingStateTableItem
});

/* eslint-disable-next-line max-len */
export const ComponentBodyViewFormsTopNo = ComponentBodyViewTopItemsRow.extend({
    template: '#tpl_component_bodyview_forms_top_nos_row',

    templateHelpers: function () {
        var supers = ComponentBodyViewFormsTopNo.__super__.templateHelpers.apply(this, arguments);

        return $.extend(supers, {
            form_field_title: this.model.get('info').form_field.title
        });
    }
});

/* eslint-disable-next-line max-len */
export const ComponentBodyViewFormsTopNos = ComponentBodyViewTopItems.extend({
    show_default_four_weeks_header: true,
    itemView: ComponentBodyViewFormsTopNo
});

/* eslint-disable-next-line max-len */
export const ComponentFormsTopNos = DashboardCardComponent.extend({
    query_key: 'forms_top_nos',
    view_class: ComponentBodyViewFormsTopNos,
    loading_state_view_class: ComponentLoadingStateTableItem
});

/* eslint-disable-next-line max-len */
export const ComponentBodyViewTaskCompletionRateSummary = ComponentBodyViewTaskCompletionRate.extend({
    template: '#tpl_component_body_view_task_completion_rate_summary',

    setup_chart_options: function () {
        ComponentBodyViewTaskCompletionRateSummary.__super__.setup_chart_options.apply(this, arguments);

        this.chart_options.chart.marginBottom = undefined;
        this.chart_options.chart.height = 200;
        this.chart_options.yAxis = {
            min: 0,
            max: 100,
            tickPositioner: function () {
                return [0, 20, 40, 60, 80, 100];
            },
            lineColor: '#F2F7FA',
            title: {
                enabled: false
            },
            labels: {
                formatter: function () {
                    return this.value + ' %';
                }
            }
        };
    }
});

/* eslint-disable-next-line max-len */
export const ComponentTaskCompletionRateSummary = ComponentTaskCompletionRate.extend({
    view_class: ComponentBodyViewTaskCompletionRateSummary
});

export const ComponentBodyViewBaseDataRow = ComponentBodyViewTopItemsRow.extend({
    templateHelpers: function () {
        var this_ = this;
        var supers = ComponentBodyViewBaseDataRow.__super__.templateHelpers.apply(this, arguments);

        return $.extend(supers, {
            display_statuses: $f.mobile.dashboard.is_account($f.mobile.dashboard.get_active_account_or_group()),
            headers: this.model.collection.headers,
            get_classname_for_column: this.get_classname_for_column.bind(this_),
            is_complete_with_exceptions: this.is_complete_with_exceptions.bind(this_)
        });
    },

    get_classname_for_column: function (column) {
        if (column.value > 0 && column.value < 50) {
            return 'red_column';
        }

        return '';
    },

    is_complete_with_exceptions: function (column) {
        return column.current_state === 'complete_with_exception';
    }
});

/* eslint-disable-next-line max-len */
export const ComponentBodyViewRecurringProject = ComponentBodyViewBaseDataRow.extend({
    template: '#tpl_component_bodyview_recurring_projects_row',

    templateHelpers: function () {
        var info = this.model.get('info');
        var due_time_range =
            this.frequency == $f.utils.recurring.frequencies.DAILY
                ? $f.utils.get_project_time_range(info.project)
                : null;

        return $.extend(ComponentBodyViewRecurringProject.__super__.templateHelpers.apply(this, arguments), {
            is_all_projects: info.is_all_projects || false,
            project_title: info.is_all_projects ? _t('mobile_pso_filter_all_projects') : info.project.title,
            user_role: info.is_all_projects ? '' : info.user_role.name,
            due_time_range: due_time_range
        });
    }
});

export const ComponentBodyViewLogbook = ComponentBodyViewBaseDataRow.extend({
    template: '#tpl_component_bodyview_logbook_row',

    events: {
        'click a.navlink': 'navigate_to_tasks'
    },

    templateHelpers: function () {
        var info = this.model.get('info');
        var parent_project_id = info.project.id;
        var columns = this.model.get('columns');
        var columns_adjusted_length = columns.length - 1;
        var most_recent_project_column = columns[columns_adjusted_length];
        var has_calendar_events = info.project.has_calendar_events;
        var has_multiple_tasks_per_day = info.project.has_multiple_tasks_per_day;

        var get_column_project_ids = function (column) {
            if (!column) {
                return [];
            }
            try {
                return column.info.projects.map(function (project) {
                    return project.id;
                });
            } catch (e) {
                return [];
            }
        };

        var due_time_range = null;
        if (this.frequency == $f.utils.recurring.frequencies.DAILY) {
            due_time_range = $f.utils.get_project_time_range(info.project);
        }

        var supers = ComponentBodyViewLogbook.__super__.templateHelpers.apply(this, arguments);

        const get_cell_tag = (column) => {
            const { current_state } = column;

            if (has_multiple_tasks_per_day) {
                if (column.info.total > 0) {
                    return 'a';
                }
                return 'span';
            }

            return current_state === 'incomplete_and_closed' ? 'span' : 'a';
        };

        return $.extend(supers, {
            project_title: info.project.title,
            user_role: info.user_role.name,
            most_recent_project_column,
            get_cell_tag,
            get_column_project_ids,
            frequency: this.frequency,
            parent_project_id,
            show_titles: true,
            is_column_selected: () => false,
            due_time_range,
            has_calendar_events,
            has_multiple_tasks_per_day
        });
    },

    /**
     * Generate the necessary options and dashboard cards for opening up the tasks page in the correct datepicker view
     * with aggregates
     */
    _generate_task_by_project_options: function (parent_project_id, header, frequency) {
        return {
            toolbarnav_title: this.model.get('info').project.title,
            dashboard_cards: new DashboardViewConfigurations([
                {
                    view_class: DashboardCardLogbookForTasks,
                    view_options: {
                        group_or_account: $f.mobile.dashboard.get_active_account_or_group(),
                        frequency: frequency,
                        selected_header_info: header,
                        parent_project_id: parent_project_id,
                        headers: this.model.collection.headers
                    }
                }
            ])
        };
    },

    /**
     * Navigates to a single task page
     *
     * @param data
     */
    _navigate_to_single_task: function (data) {
        var task = new MobileTask({ id: data.taskId });
        showLoadingDialog();
        task.fetch({
            success: function () {
                hideLoadingDialog();
                $f.mobile.nav.show(TaskDetailsPage, {
                    model: task,
                    toolbarnav_title: _t('mobile_common_tasks')
                });
            },
            error: function () {
                hideLoadingDialog();
                showErrorMessage('Error fetching task.');
            }
        });
    },

    /**
     * Navigates to a single submission
     *
     * @param data
     */
    _navigate_to_submission: function (data) {
        var submission = new MobileSubmission({ id: data.fulfillSubmissionId });
        showLoadingDialog();
        // @author:dem, @todo: this is copy/paste from HistoryListItem.click_navlink
        // this is repeated slightly differently in 4 places, this needs to be unified. @jira:MOBILE-569
        submission.fetch({
            success: function () {
                hideLoadingDialog();

                var expanded_metadata = submission.get('expanded_metadata');

                // We'll just delete the 'expanded_metadata key so
                // that we're not passing it to the FormStructure constructor
                submission.unset('expanded_metadata');

                $f.mobile.nav.show(SubmittedFormPage, {
                    collection: submission.get('form_structure'),
                    use_transitions: true,
                    expanded_metadata: expanded_metadata
                });
            },
            error: function () {
                hideLoadingDialog();
                showErrorMessage('Error fetching task submission.');
            }
        });
    },

    _navigate_to_location_project_tasks: function (data) {
        var search_options = new LogBookTaskSearchOptions(null, {
            persist_on_change: false // don't save every time we set a value. Instead, we'll call save_local() once at the end
        });

        var active_group_or_account = $f.mobile.dashboard.get_active_account_or_group();
        if ($f.mobile.dashboard.is_account(active_group_or_account)) {
            search_options.findWhere({ field: 'account_id' }).set({ value: active_group_or_account.id });
        } else {
            search_options.findWhere({ field: 'group_id' }).set({ value: active_group_or_account.id });
        }

        search_options.findWhere({ field: 'project_ids' }).set('value', data.projectIds);

        search_options.save_local(() => {
            $f.mobile.nav.show(
                TasksForLogbook,
                this._generate_task_by_project_options(
                    data.parentProjectId,
                    this.model.collection.headers.get(data.headerId),
                    data.frequencyType
                )
            );
        });
    },

    /**
     * Navigate off of click events to the tasks page
     * Can be clicked from 2 locations:
     *      - The project name clicks into the filtered tasks page by date
     *      - The data cell clicks into
     *          - (location) - a single task
     *          - group - the recurring project filtered by group and date
     *
     * @param event
     */
    navigate_to_tasks: function (event) {
        var this_ = this;
        var data = $(event.currentTarget).data();
        var is_project_with_multiple_tasks_per_day = $(event.currentTarget).hasClass('mtp');

        if (is_project_with_multiple_tasks_per_day) {
            return this._navigate_to_location_project_tasks(data);
        }

        // if we are on an account page, and this is an unavailable task, dont go anywhere
        if (
            ($f.mobile.dashboard.get_active_account() && data.currentState == '') ||
            ($f.mobile.dashboard.get_active_group() && data.currentValue == 0)
        ) {
            return false;
        }

        var search_options = new LogBookTaskSearchOptions(null, {
            persist_on_change: false // don't save every time we set a value. Instead, we'll call save_local() once at the end
        });

        var active_group_or_account = $f.mobile.dashboard.get_active_account_or_group();
        if ($f.mobile.dashboard.is_account(active_group_or_account)) {
            search_options.findWhere({ field: 'account_id' }).set({ value: active_group_or_account.id });
        } else {
            search_options.findWhere({ field: 'group_id' }).set({ value: active_group_or_account.id });
        }

        $f.mobile.nav.add_options({ headers: this.model.collection.headers });

        // this goes from most specific to least specific
        // task with submission, task, single cell filtered view, by row
        if (data.fulfillSubmissionId) {
            return this._navigate_to_submission(data);
        }

        if (data.taskId) {
            return this._navigate_to_single_task(data);
        }

        var project = this.model.get('info').project;
        search_options.findWhere({ field: 'project_ids' }).set({
            value: data.projectIds,
            display_value: project.title
        });

        search_options.save_local(function () {
            $f.mobile.nav.show(
                TasksForLogbook,
                this_._generate_task_by_project_options(
                    data.parentProjectId,
                    this_.model.collection.headers.get(data.headerId),
                    data.frequencyType
                )
            );
        });
    }
});

/* eslint-disable-next-line max-len */
export const ComponentBodyViewRecurringProjects = ComponentBodyViewTopItems.extend({
    show_default_four_weeks_header: true,
    itemView: ComponentBodyViewRecurringProject,
    itemViewOptions: function () {
        return {
            frequency: this.parent_component.frequency
        };
    }
});

export const ComponentBodyViewLogbooks = ComponentBodyViewTopItems.extend({
    template: '#tpl_component_bodyview_logbook',
    itemView: ComponentBodyViewLogbook,

    onRender: function () {
        this.$el.addClass('logbook_scroller');

        this.show_header(
            new MobileBaseTopItemsHeaderList({
                collection: this.collection.headers,
                card_collection: this.query_collection
            })
        );

        this.$el.find('th.empty_header_item').addClass('logbook_sticky_column');
    },

    itemViewOptions: function () {
        return {
            frequency: this.parent_component.options.frequency
        };
    }
});

export const ComponentRecurringProjects = DashboardCardComponent.extend({
    query_key: 'project_recurring_aggregate',
    view_class: ComponentBodyViewRecurringProjects,
    query_collection_class: MobileRecurringProjects,
    loading_state_view_class: ComponentLoadingStateTableItem,

    frequency: global_recurring_utils.frequencies.DAILY,

    setup_query: function () {
        // @note @author:halah every time we change the frequency for recurring projects,
        // we need to reset search option and not add another
        this.query_structure.search = [];
        ComponentRecurringProjects.__super__.setup_query.apply(this, arguments);
    },

    get_additional_search_criterias: function () {
        return [
            new SearchCriteria({
                display: '',
                field_id: 'recurring_frequency',
                operator_id: '=',
                value: this.frequency
            })
        ];
    }
});

export const ComponentLogbook = ComponentRecurringProjects.extend({
    view_class: ComponentBodyViewLogbooks,
    query_collection_class: LogbookResults,
    loading_state_view_class: ComponentLoadingStateTableItem,
    dashboard_filters_class: DashboardFilterListForLogbook,
    _infinite_scroll_locked: false,
    _infinite_scroll_finished: false, // set to true when we've reached the end of infinite scroll
    _last_scroll_width: null,

    className: function () {
        return ComponentLogbook.__super__.className.apply(this, arguments) + ' logbook';
    },

    frequency: null, // the frequency this component is showing

    setup_query: function () {
        // Make sure our dashboard_filters are constructed with the right recurring frequency
        this.dashboard_filters_class = this.dashboard_filters_class.extend({
            frequency: this.frequency
        });

        ComponentLogbook.__super__.setup_query.apply(this, arguments);

        // If this has been selected from the logbook page and the selected header isn't in the original time range
        // we need to increase the original fetch size.
        // @author:dem, note: we could reset the limit to 10 after the initial data fetch
        // but it complicates the code for a small performance gain/edge case handling
        if (this.selected_header_info) {
            this.query_collection.dashboard_filters.paginator.set(
                'limit',
                Math.max(
                    DashboardFilterListForLogbook.default_paginator_limit,
                    Math.ceil(this.selected_header_info.collection.length / 10) * 10
                )
            );
        }

        // query modifications for logbook
        this.query_collection.search_criterias.result_type.bucket_time_frame.include_total = false;
        this.query_collection.search_criterias.result_type.bucket_time_frame.bucket_frequency_type = this.frequency;

        if ($f.mobile.dashboard.is_account(this.group_or_account)) {
            // don't want to use aggregation for account level
            this.query_collection.search_criterias.result_type.bucket_time_frame.use_aggregation = false;
        }

        if (this.parent_project_id) {
            this.query_collection.search_criterias.add(
                new SearchCriteria({
                    field_id: 'parent_project_id',
                    operator_id: '=',
                    value: this.parent_project_id
                })
            );
        }
    },

    show_body_view: function (state, collection, response) {
        if (response?.results?.length === 0) {
            this._infinite_scroll_finished = true;
        }

        if (this._infinite_scroll_locked && this.r_body && this.r_body.currentView) {
            // We are re-showing the body view after fetching extra results for horizontal pagination.
            this.r_body.currentView.render(); // need to re-render so the items are in the correct order
            this._restore_scroll();
            this._set_infinite_scroll_unlocked();
            return;
        }

        this._set_infinite_scroll_unlocked();

        ComponentLogbook.__super__.show_body_view.apply(this, arguments);

        if (!this.r_body || !this.r_body.currentView) {
            return; // down state or other could have been shown in super() instead of body view
        }

        this._setup_infinite_scroll();
    },

    /**
     * Only show the down state if this is the first time fetching (not re-fetching more date data)
     */
    show_down_state: function (zenput_code) {
        if (this.query_collection.length == 0) {
            return ComponentLogbook.__super__.show_down_state.apply(this, arguments);
        }
        return false;
    },

    /**
     * We don't want to show the normal down state when we're just fetching additional data for infinite scroll.
     */
    show_loading_state: function () {
        if (this._infinite_scroll_locked) {
            return;
        }

        ComponentLogbook.__super__.show_loading_state.apply(this, arguments);
    },

    _next_page: function (event) {
        if (this.query_collection.is_fetching) {
            return false;
        }

        // update paginator that controls horizontal pagination ('scrolling') and fetch
        this.query_collection.dashboard_filters.paginator.next_page();
        this.fetch_data();

        return false;
    },

    /**
     * Set scroll listeners, etc. for infinite scroll
     */
    _setup_infinite_scroll: function () {
        const $scrollable = this.$el.find('.logbook_scroller');
        const scroll_event_id = `scroll.infinite_scroll_${this.cid}`;

        this._restore_scroll();

        // timeout is necessary to give above scroll correction (@see _restore_scroll())
        // time to finish before our event listener picks it up below
        setTimeout(() => {
            $scrollable.off(scroll_event_id); // just in case
            $scrollable.on(scroll_event_id, () => {
                this._last_scroll_width = $scrollable.get(0).scrollWidth; // keep track of scroll pos to account for jump

                if (this._infinite_scroll_finished) {
                    return; // no more results to fetch
                }

                if ($scrollable.scrollLeft() > 0 || this._infinite_scroll_locked) {
                    return;
                }

                this._set_infinite_scroll_locked();
                this._next_page();
            });
        }, 10);
    },

    _restore_scroll: function () {
        var $scrollable = this.$el.find('.logbook_scroller');
        var scroll_to = $scrollable.get(0).scrollWidth; // all the way to the end (right)

        if (this._last_scroll_width) {
            // if we are re-rendering after horizontal paginating, adjust the scroll position to match where the user
            // was before we added more results.
            scroll_to = scroll_to - this._last_scroll_width;
        }

        $scrollable.scrollLeft(scroll_to);
    },

    /**
     * "locks" infinite scroll so we do not fetch multiple times before getting results back.
     *
     * @see  _infinite_scroll_locked
     * @see  _set_infinite_scroll_unlocked
     */
    _set_infinite_scroll_locked: function () {
        this._infinite_scroll_locked = true;
        this.$el.addClass('infinite_scroll_loading');
    },

    /**
     * "unlocks" infinite scroll and hides the loading spinner.
     *
     * @see  infinite_scroll_locked
     * @see  _set_infinite_scroll_locked
     */
    _set_infinite_scroll_unlocked: function () {
        this._infinite_scroll_locked = false;
        this.$el.removeClass('infinite_scroll_loading');
    }
});

/* eslint-disable-next-line max-len */
export const ComponentBodyViewLogbookForTask = ComponentBodyViewLogbook.extend({
    events: {
        'click .navlink': 'update_date_filters'
    },

    initialize: function (options) {
        ComponentBodyViewLogbookForTask.__super__.initialize.apply(this, arguments);
        // this will be re-initialized after the tll re-fetch of the collection
        if (!this.model.collection.selected_header) {
            this.model.collection.selected_header = this.headers.findWhere({
                label: this.selected_header_info.get('label')
            });
            this.center_selected = true;
        }
    },

    templateHelpers: function () {
        var this_ = this;
        return $.extend(ComponentBodyViewLogbookForTask.__super__.templateHelpers.apply(this, arguments), {
            frequency: this.frequency,
            show_titles: false,
            is_column_selected: this.is_column_selected.bind(this_)
        });
    },

    is_column_selected: function (column) {
        // @author:david @author:dem we can't use column.header_id because our selected_header ID is stale and not in
        // sync across pagination sets (e.g., [0-20,20-40] vs [0-40]). This is confusing.
        if (column.header_sort_key === this.model.collection.selected_header.get('sort_key')) {
            return 'selected';
        }

        return '';
    },

    onDomRefresh: function () {
        // handle the selected date being off the screen after the first load only
        if (!this.center_selected) {
            return;
        }

        var $selected = this.$el.find('.selected');
        if (!$selected.length) {
            $f.mobile.logging.log_error('Could not find selected header for recurring calendar view');
            return;
        }
        var scroll = $selected.offset().left - window.innerWidth / 2 + $selected.width() / 2;
        setTimeout(function () {
            $('.logbook_scroller').scrollLeft(scroll);
        }, 20);
    },

    update_date_filters: function (event) {
        var data = $(event.currentTarget).data();
        var parent_project_id = data.parentProjectId;
        var frequency = data.frequencyType;
        var header = this.model.collection.headers.get(data.headerId);

        if (
            ($f.mobile.dashboard.get_active_account() && data.currentState == '') ||
            ($f.mobile.dashboard.get_active_group() && data.currentValue == 0)
        ) {
            return false;
        }

        this.model.collection.selected_header = header;
        this.model.collection.trigger('change');
        // Regenerate the options so if we drill down, the back button takes us to the same view
        var options = this._generate_task_by_project_options(parent_project_id, header, frequency);
        $f.mobile.nav.add_options(options);

        var search_options = new LogBookTaskSearchOptions(null, { persist_on_change: false });
        // need to update the cached version
        search_options.fetch_from_local(function () {
            search_options.findWhere({ field: 'project_ids' }).set({
                value: data.projectIds
            });

            search_options.save_local(function () {
                $f.application.vent.trigger('dashboard:tasks:rerender_tasks');
            });
        });

        this.center_selected = false;
        this.render();
    }
});

export const ComponentBodyViewLogbookForTasks = ComponentBodyViewLogbooks.extend({
    itemView: ComponentBodyViewLogbookForTask,
    className: 'logbook_for_tasks',

    onRender: function () {
        ComponentBodyViewLogbookForTasks.__super__.onRender.apply(this, arguments);
        // when on the tasks page, use the entire width for scrolling dates since we aren't showing the project title
        this.$el.addClass('logbook_scroller_full_width');
        // remove the blank empty header item that is normally a placeholder for the title
        this.$el.find('th.empty_header_item').remove();
    },

    itemViewOptions: function () {
        return {
            frequency: this.parent_component.frequency,
            selected_header_info: this.parent_component.selected_header_info,
            headers: this.parent_component.headers
        };
    }
});

export const ComponentLogbookForTasks = ComponentLogbook.extend({
    view_class: ComponentBodyViewLogbookForTasks
});

//  ** Filters ** //
export const MobileDashboardCardFilterForm = Backbone.Marionette.Layout.extend({
    template: '#tpl_dashboard_card_filter_form',
    i18n_key_placeholder: null, // translation key
    query_key: null,
    form_template_title: null,

    regions: {
        r_form_templates_typeahead: '.r_form_templates_typeahead'
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        if (!this.form_template_title && this._get_form_search_from_structure()) {
            this.form_template_title = this._get_form_search_from_structure().display;
        }
    },

    onRender: function () {
        var this_ = this;
        var search_structure = DashboardSearchStructures[this_.query_key];

        this.r_form_templates_typeahead.show(
            new FormTemplateTypeahead({
                is_create_context: false,
                i18n_key_placeholder: this_.i18n_key_placeholder,

                get_default_value: function () {
                    return this_.form_template_title || '';
                },
                post_on_select: function (selection) {
                    this_.form_template_title = selection.get('title');

                    // @author:david might be better to convert our search criteria structure into a proper
                    // SearchCriterias() and work on it as that, then export it back via toJSON() to live
                    // on the global $f.mobile.dashboard_search_structures, but this seems fine for now:

                    var search = this_._get_form_search_from_structure() || {};

                    $.extend(search, {
                        display: selection.get('title'),
                        field_id: 'form_template',
                        operator_id: '=',
                        value: selection.id
                    });

                    // add search object to list if it wasn't already there
                    if (!this_._get_form_search_from_structure()) {
                        search_structure.search.push(search);
                    }

                    this_.trigger('filter_change', selection);
                },
                on_clear: function () {
                    this_.form_template_title = null;

                    // remove form_template search criteria from list
                    search_structure.search = _.without(
                        search_structure.search,
                        this_._get_form_search_from_structure()
                    );
                    this_.trigger('filter_change');
                }
            })
        );
    },

    _get_form_search_from_structure: function () {
        return _.findWhere(DashboardSearchStructures[this.query_key].search, {
            field_id: 'form_template'
        });
    }
});

export const DashboardCardFilterRecurringProjects = Backbone.Marionette.ItemView.extend({
    template: '#tpl_dashboard_card_filter_recurring_projects',
    className: 'recurring_projects_filter_container',
    frequency: global_recurring_utils.frequencies.DAILY, // default is daily

    events: {
        'click .recurring_project_filter': 'update_filter'
    },

    templateHelpers: function () {
        return {
            frequency: this.frequency,
            frequency_daily: _t('mobile_dashboard_card_filter_daily'),
            frequency_weekly: _t('mobile_dashboard_card_filter_weekly'),
            frequency_monthly: _t('mobile_dashboard_card_filter_monthly')
        };
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
    },

    update_filter: function (event) {
        this.frequency = $(event.target).data('frequency');
        this.trigger('filter_change', this.frequency);

        return false;
    }
});
// ** Cards ** //

/**
 * Show a downstate message that the user is not assigned to any teams
 */

export const DashboardCardNoGroupsDownstate = MobileDashboardCard.extend({
    get_title: function () {
        return _t('mobile_common_tasks_my');
    },

    components: [ComponentMyTasks, ComponentNoGroupsDownState]
});

/**
 * This is the dashboard card for either an account or a group
 */
export const DashboardCardCurrentTasks = MobileDashboardCard.extend({
    components: [ComponentTaskCompletionRate, ComponentTasks],

    initialize: function (options) {
        DashboardCardCurrentTasks.__super__.initialize.apply(this, arguments);

        // Allow clicking on header if our card's team isn't the same team the page is on
        this.allow_click_header =
            this.view_options.group_or_account.id !== $f.mobile.dashboard.get_active_account_or_group().id;

        if (this.view_options.is_sub_group) {
            this.$el.addClass('subgroup');
        }
    },

    get_title: function () {
        return this.view_options.group_or_account.get('name');
    },

    on_header_click: function () {
        $f.mobile.nav.show(MobileDashboard, {
            active_account_or_group: this.view_options.group_or_account
        });
    }
});

export const DashboardCardMyTasks = MobileDashboardCard.extend({
    get_title: function () {
        return _t('mobile_common_tasks_my');
    },

    components: [ComponentMyTasks]
});

export const DashboardCardTasksCurrentGroup = MobileDashboardCard.extend({
    get_title: function () {
        return _t('mobile_common_tasks_for') + ' ' + this.view_options.group_or_account.get('name');
    },

    components: [ComponentTasks]
});

export const MobileDashboardCardTopTriggeredWorkflows = MobileDashboardCard.extend({
    get_title: function () {
        return 'Triggers';
    },

    components: [ComponentTopTriggeredWorkflows],

    show_filter_view: function () {
        this.r_filter.show(
            new MobileDashboardCardFilterForm({
                query_key: 'workflows_top_triggered',
                i18n_key_placeholder: 'mobile_dashboard_card_filter_form_template_placeholder'
            })
        );
    }
});

/* eslint-disable-next-line max-len */
export const ComponentBodyViewOngoingProjectItem = ComponentBodyViewTopItemsRow.extend({
    template: '#tpl_component_bodyview_ongoing_project_item_row',

    events: {
        'click .navlink': 'navigate_to_task_list'
    },

    templateHelpers: function () {
        var relative_date_due_from_today = this.model.get('date_due')
            ? $f.utils.get_relative_date_from_today(new Date(this.model.get('date_due').$date))
            : null;
        return {
            is_overdue: relative_date_due_from_today ? relative_date_due_from_today.color === 'red' : false,
            relative_date_due: relative_date_due_from_today,
            is_recurring: this.model.get('parent_project') && this.model.get('parent_project').recurring != null,
            time_due: this.model.get_time_start_and_due_str(),
            tasks_remaining: this.model.get('num_tasks') - this.model.get('num_tasks_closed')
        };
    },

    navigate_to_task_list: function (event) {
        // Sends the currently selected search options to the task page and
        // navigates the user to the task list view

        var search_options = new ProjectTaskSearchOptions(null, {
            persist_on_change: false // don't save every time we set a value. Instead, we'll call save_local() once at the end
        });
        search_options.findWhere({ field: 'status' }).set({ value: 'open' });
        search_options
            .findWhere({ field: 'is_active' })
            .set({ value: this.model.collection.paginator.get('is_active') });
        search_options.findWhere({ field: 'filter' }).set({ value: 'all' });
        search_options
            .findWhere({ field: 'project_ids' })
            .set({ value: this.model.get('id'), display_value: this.model.get('title') });

        var account_criteria = this.model.collection.search_criterias.findWhere({ field_id: 'account_id' });
        if (account_criteria) {
            search_options
                .findWhere({ field: 'account_id' })
                .set({ value: account_criteria.get('value'), display_value: account_criteria.get('display_value') });
        }

        // Save filters to local storage and navigate to task list
        search_options.save_local(function () {
            $f.mobile.nav.show(TasksFilteredByProject);
        });
    }
});

/* eslint-disable-next-line max-len */
export const ComponentBodyViewOngoingProjectList = ComponentBodyViewTopItems.extend({
    itemView: ComponentBodyViewOngoingProjectItem,
    show_default_four_weeks_header: false
});

export const ComponentBodyViewOngoingProjectFooter = Backbone.Marionette.ItemView.extend({
    template: '#tpl_component_body_view_ongoing_projects_footer',
    legend: '',
    templateHelpers: function () {
        return {
            footer_legend: this.legend
        };
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
    }
});

export const ComponentBodyViewOngoingProjectLayout = Backbone.Marionette.Layout.extend({
    template: '#tpl_body_view_ongoing_projects_layout',
    hidden_projects: [],
    footer_status: 1,
    footer_status_enum: {
        NOOP: 1,
        SHOW_MORE: 2,
        SHOW_LESS: 3,
        GO_TO_PROJECTS: 4
    },

    events: {
        'click .project_list_footer': 'footer_action'
    },

    regions: {
        r_list: '.project_list',
        r_footer: '.project_list_footer'
    },

    onRender: function () {
        this.r_list.show(
            new ComponentBodyViewOngoingProjectList({
                collection: this.collection
            })
        );
        this.r_footer.show(
            new ComponentBodyViewOngoingProjectFooter({
                legend: this.get_footer_legend()
            })
        );
    },

    /**
     * Decides what behavior our footer should have. Depending on the number of projects that we have,
     * our footer will behave differently:
     *
     *     - Less than 5 projects: no footer
     *     - Less than 10 projects: footer says 'Show More' and will expand to 10
     *     - More than 10 projects: footer says 'Go to projects' with link to projects page
     */
    footer_action: function () {
        if (this.footer_status === this.footer_status_enum.SHOW_MORE) {
            // add hidden projects to the collection and rerender
            this.footer_status = this.footer_status_enum.SHOW_LESS;
            while (this.hidden_projects.length && this.collection.length < 10) {
                this.collection.push(this.hidden_projects.pop());
            }
            // If the total amount of projects exceeds 10, show a link to go to the projects screen
            if (this.hidden_projects.length) {
                this.footer_status = this.footer_status_enum.GO_TO_PROJECTS;
            }
        } else if (this.footer_status === this.footer_status_enum.SHOW_LESS) {
            // remove extra projects from collection
            this.footer_status = this.footer_status_enum.SHOW_MORE;
            while (this.collection.length > 5) {
                this.hidden_projects.push(this.collection.pop());
            }
        } else if (this.footer_status === this.footer_status_enum.GO_TO_PROJECTS) {
            $f.mobile.nav.show(ProjectsView, {});
            return false;
        }

        this.r_footer.show(
            new ComponentBodyViewOngoingProjectFooter({
                legend: this.get_footer_legend()
            })
        );

        return false;
    },

    /**
     * Handles the cards ability to Show More, Show Less or Go To Projects.
     */
    onBeforeRender: function () {
        // For less than 5 projects, don't show anything in the footer
        if (this.collection.length <= 5) {
            this.footer_status = this.footer_status_enum.NOOP;
            return;
        }
        // Initial status is to only show 5 projects
        while (this.collection.length > 5) {
            this.hidden_projects.push(this.collection.pop());
        }
        this.footer_status = this.footer_status_enum.SHOW_MORE;
    },

    get_footer_legend: function () {
        if (this.footer_status == this.footer_status_enum.SHOW_MORE) {
            return _t('mobile_dashboard_card_ongoing_projects_show_more');
        }

        if (this.footer_status == this.footer_status_enum.GO_TO_PROJECTS) {
            return _t('mobile_dashboard_card_ongoing_projects_go_to_projects');
        }

        if (this.footer_status == this.footer_status_enum.SHOW_LESS) {
            return _t('mobile_dashboard_card_ongoing_projects_show_less');
        }

        return '';
    }
});

export const ComponentOngoingProjects = DashboardCardComponent.extend({
    query_key: 'project_list',
    view_class: ComponentBodyViewOngoingProjectLayout,
    loading_state_view_class: ComponentLoadingStateTableItem,

    fetch_data: function () {
        // using limit of 11 because of weird pagination behavior,
        // @see ComponentBodyViewOngoingProjectLayout.footer_action()
        this.query_collection.paginator.set({
            limit: 11,
            start: 0
        });
        ComponentOngoingProjects.__super__.fetch_data.apply(this, arguments);
    }
});

export const MobileDashboardCardFormsTopNos = MobileDashboardCard.extend({
    get_title: function () {
        return _t('mobile_dashboard_card_title_forms_top_nos');
    },

    components: [ComponentFormsTopNos],

    show_filter_view: function () {
        this.r_filter.show(
            new MobileDashboardCardFilterForm({
                query_key: 'forms_top_nos',
                i18n_key_placeholder: 'mobile_task_tab_form'
            })
        );
    }
});

export const DashboardCardTaskCompletionRate = MobileDashboardCard.extend({
    get_title: function () {
        return _t('mobile_dashboard_tcr_graph_name');
    },

    components: [ComponentTaskCompletionRateSummary]
});

export const DashboardCardOngoingProjects = MobileDashboardCard.extend({
    get_title: function () {
        return _t('mobile_dashboard_card_ongoing_projects_title');
    },

    components: [ComponentOngoingProjects]
});

export const DashboardCardRecurringProjects = MobileDashboardCard.extend({
    get_title: function () {
        return _t('mobile_dashboard_card_title_recurring_projects');
    },

    components: [ComponentRecurringProjects],

    show_filter_view: function () {
        this.r_filter.show(
            new DashboardCardFilterRecurringProjects({
                frequency: this.view_options.frequency
            })
        );
    }
});

export const DashboardCardLogbook = MobileDashboardCard.extend({
    show_info_icon: true,

    events: {
        'click .show_legend': 'show_logbook_legend'
    },

    get_title: function () {
        if (this.view_options.frequency === $f.utils.recurring.frequencies.DAILY) {
            return _t('mobile_dashboard_card_filter_daily');
        }

        if (this.view_options.frequency === $f.utils.recurring.frequencies.WEEKLY) {
            return _t('mobile_dashboard_card_filter_weekly');
        }

        if (this.view_options.frequency === $f.utils.recurring.frequencies.MONTHLY) {
            return _t('mobile_dashboard_card_filter_monthly');
        }
    },

    components: [ComponentLogbook],

    show_logbook_legend: function (event) {
        $f.application.content_overlay.show(
            new TasksLegendModal({
                title: _t('mobile_tasks_legend_modal_title')
            })
        );

        return false;
    }
});

export const DashboardCardLogbookForTasks = DashboardCardLogbook.extend({
    show_info_icon: false,
    show_header: false,

    components: [ComponentLogbookForTasks]
});

export const BannersCarousel = Backbone.Marionette.Layout.extend({
    template: '#tpl_dashboard_banners',
    collection: null, // MobileDashboard.banners

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        this.listenTo(this.collection, 'sync', this.render);
    },

    onRender: function () {
        if (this.collection.length === 0) {
            return;
        }

        this.render_carousel();
    },

    click_banner_details: function (banner_index) {
        const banner = this.collection.at(banner_index);

        $f.mobile.nav.show(BannerDetailsPage, {
            model: banner
        });
    },

    render_carousel: function () {
        const elem = this.el.querySelector('.banners_carousel');
        const count = this.collection.length;
        const bannersData = this.collection.map((model, index) => ({
            ...model.toJSON(),
            count,
            index,
            handleBannerDetailsClick: this.click_banner_details.bind(this)
        }));

        window.mountReactJSX(elem, () => (
            <Carousel>
                {bannersData.map((item, index) => (
                    <AnnouncementBanner key={index} {...item} />
                ))}
            </Carousel>
        ));
    }
});
export const BannerDetailsPage = BaseToolbarNavPageLayout.extend({
    className: 'banner_detail_page',
    template: '#tpl_banner_detail_page',
    is_top_level_page: false,

    model: null, // Banner

    initialize: function () {
        this.mark_as_read();
    },

    mark_as_read: function (options) {
        var _ajax = $f.utils.select_ajax();

        _ajax({
            url: `/api/v3/banners/${this.model.get('id')}/mark_as_read/`,
            dataType: 'json',
            type: 'POST',
            cache: false,
            contentType: 'application/json'
        });
    },

    toolbarnav_title: function () {
        return this.model.get('type_translation');
    },

    onRender: function () {
        const banner = this.model.toJSON();
        const elem = this.el.querySelector('.banner_details');

        window.mountReactJSX(elem, () => (
            <AnnouncementBannerDetails
                baseUrl={process.env.API_URL}
                company={banner.label}
                created_user={banner.created_user}
                date_scheduled={banner.date_created}
                title={banner.title}
                message={banner.message}
                attachments={banner.attachments}
            />
        ));
    }
});
